import { supabase } from "@/lib/supabase";
import type {
  AppRole,
  ApprovalRule,
  Asset,
  AssetCandidate,
  AuditEvent,
  BankStatementImport,
  BankStatementLine,
  Budget,
  BudgetAlert,
  BusinessUnit,
  CapitalisationPolicy,
  Contract,
  DirectPurchasePolicy,
  GoodsReceipt,
  GuidedBuyingDecision,
  GuidedBuyingWorkspace,
  InternalControlRule,
  InternalControlPolicyVersion,
  InternalControlsWorkspace,
  InventoryItem,
  ImportKind,
  ImportPreview,
  ImportResult,
  Invoice,
  Location,
  NetworkRequirementSummary,
  Organisation,
  OrganisationSubscription,
  OrganisationLink,
  PurchaseOrder,
  PurchaseOrderMessage,
  PendingApprovalReassignment,
  Quote,
  QuotationEvidence,
  ReorderTemplate,
  RequirementCampaign,
  Requisition,
  AssistedExtraction,
  RequestInboxAddress,
  RequestInboxMessage,
  Rfq,
  SupplierAccess,
  SupplierFulfilmentUpdate,
  SupplierInvoiceSubmission,
  SupplierMessage,
  TeamMember,
  Vendor,
} from "@/lib/types";
import type { TenantBranding } from "@/lib/branding";
import { DEFAULT_TENANT_BRANDING, safeBrandColour } from "@/lib/branding";

function unwrap<T>(data: T | null, error: { message: string } | null): T {
  if (error) throw new Error(error.message);
  if (data === null) throw new Error("RAQOZ returned no data");
  return data;
}

async function unwrapFunction<T>(data: T | null, error: unknown): Promise<T> {
  if (error) {
    let message = error instanceof Error ? error.message : "RAQOZ request failed";
    const context = (error as { context?: Response }).context;
    if (context) {
      try {
        const payload = await context.clone().json() as { error?: unknown };
        if (payload.error) message = String(payload.error);
      } catch {
        // Keep the bounded Functions error when the response has no JSON body.
      }
    }
    throw new Error(message);
  }
  if (data === null) throw new Error("RAQOZ returned no data");
  return data;
}

type SupplierInvoiceAttachment = NonNullable<
  SupplierInvoiceSubmission["attachments"]
> & { id: string };

function joinSupplierInvoiceEvidence(
  submissions: SupplierInvoiceSubmission[],
  attachments: SupplierInvoiceAttachment[],
) {
  const evidenceById = new Map(
    attachments.map(({ id, ...evidence }) => [id, evidence]),
  );
  return submissions.map((submission) => ({
    ...submission,
    attachments: evidenceById.get(submission.original_attachment_id) || null,
  }));
}

export async function loadWorkspace() {
  const { data: auth, error: authError } = await supabase.auth.getUser();
  if (authError || !auth.user) throw new Error("Authentication required");
  const [
    { data: organisations, error: orgError },
    { data: memberships, error: memberError },
    { data: platformAdmin, error: adminError },
    { data: supplierAccess, error: supplierError },
    { data: subscriptions, error: subscriptionError },
  ] = await Promise.all([
    supabase
      .from("organisations")
      .select("id,name,legal_name,slug,gstin,settings")
      .eq("active", true)
      .order("name"),
    supabase
      .from("organisation_memberships")
      .select("organisation_id,role,status")
      .eq("user_id", auth.user.id)
      .eq("status", "active"),
    supabase
      .from("platform_administrators")
      .select("user_id")
      .eq("user_id", auth.user.id)
      .eq("active", true)
      .maybeSingle(),
    supabase
      .from("vendor_users")
      .select(
        "organisation_id,vendor_id,status,job_title,vendors(*),organisations(id,name,slug,settings)",
      )
      .eq("user_id", auth.user.id)
      .eq("status", "active"),
    supabase
      .from("organisation_subscriptions")
      .select("organisation_id,status,entitlement_snapshot,commercial_plans(code,name,availability)"),
  ]);
  if (orgError || memberError || adminError || supplierError || subscriptionError)
    throw new Error(
      orgError?.message ||
        memberError?.message ||
        adminError?.message ||
        supplierError?.message ||
        subscriptionError?.message,
    );
  return {
    user: {
      id: auth.user.id,
      email: auth.user.email || "",
      name: String(
        auth.user.user_metadata.full_name || auth.user.email || "RAQOZ user",
      ),
    },
    organisations: (organisations || []) as Organisation[],
    memberships: (memberships || []) as {
      organisation_id: string;
      role: AppRole;
      status: string;
    }[],
    platformOwner: Boolean(platformAdmin),
    supplierAccess: (supplierAccess || []) as unknown as SupplierAccess[],
    subscriptions: (subscriptions || []) as unknown as OrganisationSubscription[],
  };
}

export async function organisationPlanWorkspace(organisationId: string) {
  return rpc<{
    subscription: any;
    plans: any[];
    requests: any[];
    checkout_sessions: any[];
    price_book: string;
    billing_providers: ("stripe" | "razorpay")[];
    billing_boundary: string;
  }>("organisation_plan_workspace", { p_organisation_id: organisationId });
}

export async function createSubscriptionCheckout(
  organisationId: string,
  input: {
    provider: "stripe" | "razorpay";
    planCode: string;
    cycle: "monthly" | "annual";
  },
) {
  const { data, error } = await supabase.functions.invoke(
    "create-billing-checkout",
    {
      body: { organisationId, ...input },
    },
  );
  if (error) {
    let message = error.message;
    const context = (error as unknown as { context?: Response }).context;
    if (context) {
      try {
        const payload = await context.clone().json();
        message = String(payload?.error || message);
      } catch {
        // Keep the provider-neutral Functions error when the response has no JSON body.
      }
    }
    throw new Error(message);
  }
  if (data?.error) throw new Error(String(data.error));
  if (!data?.checkoutUrl) throw new Error("Billing provider returned no checkout URL");
  return data as {
    checkoutUrl: string;
    checkoutId: string;
    provider: "stripe" | "razorpay";
    planCode: string;
    cycle: "monthly" | "annual";
  };
}

export async function requestOrganisationPlanChange(
  organisationId: string,
  planCode: string,
  reason: string,
) {
  return rpc<Record<string, unknown>>("request_organisation_plan_change", {
    p_organisation_id: organisationId,
    p_plan_code: planCode,
    p_reason: reason,
  });
}

export async function decideOrganisationPlanChange(
  organisationId: string,
  requestId: string,
  input: {
    decision: "approved" | "rejected";
    reason: string;
    externalReference?: string;
    currentPeriodEndsAt?: string;
  },
) {
  return rpc<Record<string, unknown>>("decide_organisation_plan_change", {
    p_organisation_id: organisationId,
    p_request_id: requestId,
    p_decision: input.decision,
    p_reason: input.reason,
    p_external_reference: input.externalReference || null,
    p_current_period_ends_at: input.currentPeriodEndsAt || null,
  });
}

export async function dashboard(organisationId: string) {
  const [requisitions, rfqs, orders, invoices, audit, budgetAlerts, budgets] =
    await Promise.all([
      listRequisitions(organisationId),
      listRfqs(organisationId),
      listPurchaseOrders(organisationId),
      listInvoices(organisationId),
      listAudit(organisationId, 50),
      listBudgetAlerts(organisationId),
      listBudgets(organisationId),
    ]);
  return {
    metrics: {
      pendingApprovals: requisitions.filter((row) => row.status === "submitted")
        .length,
      activeRfqs: rfqs.filter((row) => row.status === "published").length,
      openOrders: orders.filter((row) =>
        ["issued", "part_received"].includes(row.status),
      ).length,
      invoiceExceptions: invoices.filter((row) => row.status === "exception")
        .length,
      committedSpend: orders
        .filter((row) => row.status !== "draft")
        .reduce((sum, row) => sum + Number(row.total_base || row.total), 0),
      budgetWarnings: budgetAlerts.filter((row) => !row.acknowledged_at).length,
    },
    audit,
    requisitions,
    rfqs,
    orders,
    invoices,
    budgets,
  };
}

export async function listLocations(organisationId: string) {
  const { data, error } = await supabase
    .from("locations")
    .select("*")
    .eq("organisation_id", organisationId)
    .eq("active", true)
    .order("name");
  return unwrap(data, error) as Location[];
}
export async function listApprovalRules(organisationId: string) {
  return rpc<ApprovalRule[]>("approval_requirement_catalog", {
    p_organisation_id: organisationId,
  });
}
export async function listVendors(organisationId: string) {
  const { data, error } = await supabase
    .from("vendors")
    .select("*")
    .eq("organisation_id", organisationId)
    .order("name");
  return unwrap(data, error) as Vendor[];
}
export async function listRequisitions(
  organisationId: string,
  status?: string,
) {
  let query = supabase
    .from("requisitions")
    .select(
      "*,requisition_lines(*),locations(name),profiles!requisitions_created_by_fkey(full_name),requisition_approval_assignments(id,requisition_id,approver_id,position,assigned_by,assigned_at,reassignment_required_at,reassignment_reason,approver:profiles!requisition_approval_assignments_approver_id_fkey(full_name,email))",
    )
    .eq("organisation_id", organisationId)
    .order("created_at", { ascending: false });
  if (status) query = query.eq("status", status);
  const { data, error } = await query;
  return unwrap(data, error) as unknown as Requisition[];
}
export async function loadRequestInbox(organisationId: string) {
  const [{ data: addresses, error: addressError }, { data: messages, error: messageError }] =
    await Promise.all([
      supabase
        .from("request_inbox_addresses")
        .select("id,organisation_id,address,provider,enabled")
        .eq("organisation_id", organisationId)
        .maybeSingle(),
      supabase
        .from("request_inbox_messages")
        .select("id,organisation_id,sender_email,sender_name,recipient_email,subject,text_body,raw_sha256,received_at,status,quarantine_reason,converted_requisition_id,request_inbox_attachments(id,message_id,file_name,mime_type,byte_size,sha256,object_path)")
        .eq("organisation_id", organisationId)
        .order("received_at", { ascending: false }),
    ]);
  if (addressError || messageError) throw new Error(addressError?.message || messageError?.message);
  return {
    address: (addresses || null) as RequestInboxAddress | null,
    messages: (messages || []) as unknown as RequestInboxMessage[],
  };
}

export async function requestInboxAttachmentUrl(objectPath: string) {
  const { data, error } = await supabase.storage.from("raqoz-documents").createSignedUrl(objectPath, 60);
  if (error || !data?.signedUrl) throw new Error(error?.message || "Evidence URL is unavailable");
  return data.signedUrl;
}

export async function listAssistedExtractions(
  organisationId: string,
  sourceKind: "request_inbox_attachment" | "attachment",
  sourceId: string,
) {
  const sourceColumn = sourceKind === "request_inbox_attachment"
    ? "request_inbox_attachment_id"
    : "attachment_id";
  const { data, error } = await supabase
    .from("assisted_extractions")
    .select("*,assisted_extraction_fields(*)")
    .eq("organisation_id", organisationId)
    .eq(sourceColumn, sourceId)
    .order("created_at", { ascending: false });
  return unwrap(data, error) as unknown as AssistedExtraction[];
}

export async function startAssistedExtraction(
  organisationId: string,
  sourceKind: "request_inbox_attachment" | "attachment",
  sourceId: string,
) {
  const { data, error } = await supabase.functions.invoke("extract-procurement-document", {
    body: { organisationId, sourceKind, sourceId },
  });
  if (error) throw new Error(error.message);
  if (data?.error) throw new Error(data.error);
  return data as { extractionId: string; status: string };
}

export async function confirmAssistedExtraction(
  organisationId: string,
  extractionId: string,
  payload: Record<string, unknown>,
) {
  return rpc<AssistedExtraction>("confirm_assisted_extraction", {
    p_organisation_id: organisationId,
    p_extraction_id: extractionId,
    p_confirmed_payload: payload,
  });
}
export async function listRfqs(organisationId: string) {
  const { data, error } = await supabase
    .from("rfqs")
    .select("*,requisitions(request_no,estimated_value)")
    .eq("organisation_id", organisationId)
    .order("created_at", { ascending: false });
  return unwrap(data, error) as unknown as Rfq[];
}
export async function rfqDetail(organisationId: string, rfqId: string) {
  const [
    { data: rfq, error: rfqError },
    { data: vendors, error: vendorError },
    { data: quotes, error: quoteError },
  ] = await Promise.all([
    supabase
      .from("rfqs")
      .select("*,requisitions(*,requisition_lines(*))")
      .eq("organisation_id", organisationId)
      .eq("id", rfqId)
      .single(),
    supabase
      .from("rfq_vendors")
      .select("vendor_id,vendors(*)")
      .eq("organisation_id", organisationId)
      .eq("rfq_id", rfqId),
    supabase
      .from("quotes")
      .select("*,vendors(name,rating),quote_lines(*)")
      .eq("organisation_id", organisationId)
      .eq("rfq_id", rfqId)
      .eq("is_current", true)
      .order("total"),
  ]);
  if (rfqError || vendorError || quoteError)
    throw new Error(
      rfqError?.message || vendorError?.message || quoteError?.message,
    );
  return {
    rfq: rfq as any,
    vendors: (vendors || []).map((row: any) => row.vendors) as Vendor[],
    quotes: (quotes || []) as unknown as Quote[],
  };
}
export async function listPurchaseOrders(organisationId: string) {
  const { data, error } = await supabase
    .from("purchase_orders")
    .select("*,vendors(name),rfqs(rfq_no,requisitions(request_no)),po_lines(*)")
    .eq("organisation_id", organisationId)
    .order("created_at", { ascending: false });
  return unwrap(data, error) as unknown as PurchaseOrder[];
}
export async function listDirectPurchasePolicies(organisationId: string) {
  const { data, error } = await supabase
    .from("direct_purchase_policies")
    .select("*,business_units(name,code),locations(name)")
    .eq("organisation_id", organisationId)
    .order("max_total_base");
  return unwrap(data, error) as unknown as DirectPurchasePolicy[];
}
export async function listReceipts(organisationId: string) {
  const { data, error } = await supabase
    .from("goods_receipts")
    .select(
      "*,purchase_orders(po_no,vendors(name),rfqs(requisitions(request_no))),profiles!goods_receipts_received_by_fkey(full_name),goods_receipt_lines(*,po_lines(description,unit))",
    )
    .eq("organisation_id", organisationId)
    .order("received_at", { ascending: false });
  return unwrap(data, error) as unknown as GoodsReceipt[];
}
export async function listInvoices(organisationId: string) {
  const { data, error } = await supabase
    .from("invoices")
    .select("*,purchase_orders(po_no,total,rfqs(requisitions(request_no))),vendors(name)")
    .eq("organisation_id", organisationId)
    .order("created_at", { ascending: false });
  return unwrap(data, error) as unknown as Invoice[];
}
export async function listSupplierInvoiceSubmissions(organisationId: string) {
  const [submissions, attachments] = await Promise.all([
    supabase
      .from("supplier_invoice_submissions")
      .select("*,purchase_orders(po_no),vendors(name)")
      .eq("organisation_id", organisationId)
      .order("submitted_at", { ascending: false }),
    supabase
      .from("attachments")
      .select("id,object_path,file_name,mime_type")
      .eq("organisation_id", organisationId)
      .eq("entity_type", "supplier_invoice_submission"),
  ]);
  if (submissions.error || attachments.error)
    throw new Error(submissions.error?.message || attachments.error?.message);
  return joinSupplierInvoiceEvidence(
    (submissions.data || []) as unknown as SupplierInvoiceSubmission[],
    (attachments.data || []) as SupplierInvoiceAttachment[],
  );
}
export async function listPurchaseOrderMessages(organisationId: string) {
  const { data, error } = await supabase
    .from("purchase_order_messages")
    .select("*,purchase_orders(po_no),vendors(name),profiles!purchase_order_messages_created_by_fkey(full_name,email)")
    .eq("organisation_id", organisationId)
    .order("created_at", { ascending: true });
  return unwrap(data, error) as unknown as PurchaseOrderMessage[];
}
export async function listSupplierFulfilmentUpdates(organisationId: string) {
  const { data, error } = await supabase
    .from("fulfilment_records")
    .select("*,commercial_instruments(source_purchase_order_id),profiles!fulfilment_records_recorded_by_fkey(full_name,email)")
    .eq("organisation_id", organisationId)
    .order("created_at", { ascending: false });
  return (unwrap(data, error) as unknown as SupplierFulfilmentUpdate[]).filter(
    (row) => row.details?.submitted_by_supplier === true,
  );
}
export async function listTeam(organisationId: string) {
  const rows = await rpc<
    (Omit<TeamMember, "profiles"> & { full_name: string; email: string })[]
  >("get_organisation_member_administration", {
    p_organisation_id: organisationId,
  });
  return rows.map(({ full_name, email, ...row }) => ({
    ...row,
    profiles: { full_name, email },
  }));
}

export async function listEligibleRequisitionApprovers(
  organisationId: string,
) {
  const rows = await rpc<
    (Pick<
      TeamMember,
      "organisation_id" | "user_id" | "role" | "status"
    > & { full_name: string; email: string })[]
  >("list_eligible_requisition_approvers", {
    p_organisation_id: organisationId,
  });
  return rows.map(({ full_name, email, ...row }) => ({
    ...row,
    profiles: { full_name, email },
  })) as TeamMember[];
}
export async function listPendingApprovalReassignments(
  organisationId: string,
) {
  return rpc<PendingApprovalReassignment[]>(
    "get_pending_member_approval_reassignments",
    { p_organisation_id: organisationId },
  );
}
export async function listAudit(organisationId: string, limit = 200) {
  const { data, error } = await supabase
    .from("audit_events")
    .select("*,profiles(full_name)")
    .eq("organisation_id", organisationId)
    .order("created_at", { ascending: false })
    .limit(limit);
  return unwrap(data, error) as unknown as AuditEvent[];
}

export async function listContracts(organisationId: string) {
  const { data, error } = await supabase
    .from("contracts")
    .select("*,vendors(name),contract_items(*)")
    .eq("organisation_id", organisationId)
    .order("created_at", { ascending: false });
  return unwrap(data, error) as unknown as Contract[];
}
export async function listReorderTemplates(organisationId: string) {
  const { data, error } = await supabase
    .from("reorder_templates")
    .select(
      "*,locations(name),contract_items(*,contracts(contract_no,currency,vendors(name)))",
    )
    .eq("organisation_id", organisationId)
    .order("created_at", { ascending: false });
  return unwrap(data, error) as unknown as ReorderTemplate[];
}
export async function listInventory(organisationId: string) {
  const { data, error } = await supabase
    .from("replenishment_work_queue")
    .select("*")
    .eq("organisation_id", organisationId)
    .order("name");
  return unwrap(data, error) as unknown as InventoryItem[];
}
export async function listAssets(organisationId: string) {
  const { data, error } = await supabase
    .from("assets")
    .select(
      "*,locations(name),vendors(name),custodian:profiles!assets_custodian_id_fkey(full_name),business_owner:profiles!assets_business_owner_id_fkey(full_name),maintenance_owner:profiles!assets_maintenance_owner_id_fkey(full_name),finance_owner:profiles!assets_finance_owner_id_fkey(full_name),asset_custody_events(*)",
    )
    .eq("organisation_id", organisationId)
    .order("created_at", { ascending: false });
  return unwrap(data, error) as unknown as Asset[];
}
export async function assetConsumableWorkspace(organisationId: string) {
  return rpc<any>("asset_consumable_workspace", {
    p_organisation_id: organisationId,
  });
}
export async function createAssetConsumableLink(
  organisationId: string,
  input: {
    assetId: string;
    inventoryItemId: string;
    relationshipKind: string;
    manufacturerReference: string;
    compatibilityBasis: string;
    testsOrUsesPerUnit: number | null;
    expectedMonthlyConsumption: number;
    minimumCoverageDays: number;
  },
) {
  return rpc<any>("create_asset_consumable_link", {
    p_organisation_id: organisationId,
    p_asset_id: input.assetId,
    p_inventory_item_id: input.inventoryItemId,
    p_relationship_kind: input.relationshipKind,
    p_manufacturer_reference: input.manufacturerReference || null,
    p_compatibility_basis: input.compatibilityBasis,
    p_tests_or_uses_per_unit: input.testsOrUsesPerUnit,
    p_expected_monthly_consumption: input.expectedMonthlyConsumption,
    p_minimum_coverage_days: input.minimumCoverageDays,
  });
}
export async function decideAssetConsumableLink(
  organisationId: string,
  linkId: string,
  decision: "approved" | "rejected" | "suspended",
  reason: string,
) {
  return rpc<any>("decide_asset_consumable_link", {
    p_organisation_id: organisationId,
    p_link_id: linkId,
    p_decision: decision,
    p_reason: reason,
  });
}
export async function registerInventoryLot(
  organisationId: string,
  input: {
    inventoryItemId: string;
    lotNumber: string;
    quantity: number;
    receivedOn: string | null;
    expiresOn: string | null;
    manufacturerBatchReference: string;
  },
) {
  return rpc<any>("register_inventory_lot", {
    p_organisation_id: organisationId,
    p_inventory_item_id: input.inventoryItemId,
    p_lot_number: input.lotNumber,
    p_quantity: input.quantity,
    p_received_on: input.receivedOn,
    p_expires_on: input.expiresOn,
    p_manufacturer_batch_reference: input.manufacturerBatchReference || null,
  });
}
export async function recordAssetConsumption(
  organisationId: string,
  input: {
    linkId: string;
    lotId: string | null;
    quantity: number;
    testsOrUsesCompleted: number | null;
    purpose: string;
  },
) {
  return rpc<any>("record_asset_consumption", {
    p_organisation_id: organisationId,
    p_link_id: input.linkId,
    p_lot_id: input.lotId,
    p_quantity: input.quantity,
    p_tests_or_uses_completed: input.testsOrUsesCompleted,
    p_purpose: input.purpose,
  });
}
export async function listAssetCandidates(organisationId: string) {
  const { data, error } = await supabase
    .from("asset_candidates")
    .select(
      "*,goods_receipts(grn_no),purchase_orders(po_no),vendors(name),locations(name)",
    )
    .eq("organisation_id", organisationId)
    .neq("state", "cancelled")
    .order("created_at", { ascending: false });
  return unwrap(data, error) as unknown as AssetCandidate[];
}
export async function getCapitalisationPolicy(organisationId: string) {
  const { data, error } = await supabase
    .from("capitalisation_policies")
    .select("*")
    .eq("organisation_id", organisationId)
    .single();
  return unwrap(data, error) as unknown as CapitalisationPolicy;
}
export async function listRequirementCampaigns(organisationId: string) {
  const { data, error } = await supabase
    .from("requirement_campaigns")
    .select(
      "*,requirement_campaign_items(*),requirement_campaign_recipients(*,vendors(name))",
    )
    .eq("organisation_id", organisationId)
    .order("created_at", { ascending: false });
  return unwrap(data, error) as unknown as RequirementCampaign[];
}
export async function listBusinessUnits(organisationId: string) {
  const { data, error } = await supabase
    .from("business_units")
    .select("*,locations(name)")
    .eq("organisation_id", organisationId)
    .order("code");
  return unwrap(data, error) as unknown as BusinessUnit[];
}
export async function listBudgets(organisationId: string) {
  const { data, error } = await supabase
    .from("budgets")
    .select("*,business_units(name,code),budget_commitments(amount,stage)")
    .eq("organisation_id", organisationId)
    .order("fiscal_year", { ascending: false });
  return unwrap(data, error) as unknown as Budget[];
}
export async function listBudgetAlerts(organisationId: string) {
  const { data, error } = await supabase
    .from("budget_alerts")
    .select("*,requisitions(request_no,title)")
    .eq("organisation_id", organisationId)
    .order("created_at", { ascending: false });
  return unwrap(data, error) as unknown as BudgetAlert[];
}
export async function listOrganisationLinks(organisationId: string) {
  const { data, error } = await supabase
    .from("organisation_links")
    .select(
      "*,parent:organisations!organisation_links_parent_organisation_id_fkey(name,slug),child:organisations!organisation_links_child_organisation_id_fkey(name,slug)",
    )
    .or(
      `parent_organisation_id.eq.${organisationId},child_organisation_id.eq.${organisationId}`,
    )
    .eq("active", true)
    .order("created_at", { ascending: false });
  return unwrap(data, error) as unknown as OrganisationLink[];
}
export async function networkRequirementSummary(organisationId: string) {
  return rpc<NetworkRequirementSummary[]>("network_requirement_summary", {
    p_parent_organisation_id: organisationId,
  });
}
export async function loadSupplierPortal(access: SupplierAccess) {
  const [
    rfqs,
    quotes,
    orders,
    contracts,
    messages,
    campaigns,
    acknowledgements,
    payments,
    obligations,
    coverages,
    claims,
    invoiceSubmissions,
    invoiceSubmissionAttachments,
    orderMessages,
    fulfilmentUpdates,
    supplierTrust,
    supplierNetwork,
    settlementEvidence,
  ] = await Promise.all([
    supabase
      .from("rfqs")
      .select("*,requisitions(*,requisition_lines(*))")
      .eq("organisation_id", access.organisation_id)
      .order("created_at", { ascending: false }),
    supabase
      .from("quotes")
      .select("*,quote_lines(*)")
      .eq("organisation_id", access.organisation_id)
      .eq("vendor_id", access.vendor_id)
      .order("submitted_at", { ascending: false }),
    supabase
      .from("purchase_orders")
      .select("*,po_lines(*)")
      .eq("organisation_id", access.organisation_id)
      .eq("vendor_id", access.vendor_id)
      .order("created_at", { ascending: false }),
    supabase
      .from("contracts")
      .select("*,contract_items(*)")
      .eq("organisation_id", access.organisation_id)
      .eq("vendor_id", access.vendor_id)
      .order("created_at", { ascending: false }),
    supabase
      .from("supplier_messages")
      .select("*")
      .eq("organisation_id", access.organisation_id)
      .eq("vendor_id", access.vendor_id)
      .order("created_at", { ascending: false }),
    supabase
      .from("requirement_campaigns")
      .select("*,requirement_campaign_items(*)")
      .eq("organisation_id", access.organisation_id)
      .order("created_at", { ascending: false }),
    supabase
      .from("order_acknowledgements")
      .select("*,commercial_instruments(source_purchase_order_id)")
      .eq("organisation_id", access.organisation_id)
      .eq("vendor_id", access.vendor_id)
      .order("responded_at", { ascending: false }),
    supabase.rpc("supplier_payment_status", {
      p_organisation_id: access.organisation_id,
      p_vendor_id: access.vendor_id,
    } as any),
    supabase
      .from("obligation_ledger")
      .select("*")
      .eq("organisation_id", access.organisation_id)
      .eq("vendor_id", access.vendor_id)
      .order("due_at", { ascending: true }),
    supabase
      .from("asset_coverages")
      .select("*,assets(asset_tag,name)")
      .eq("organisation_id", access.organisation_id)
      .eq("vendor_id", access.vendor_id)
      .order("ends_on", { ascending: true }),
    supabase
      .from("coverage_claims")
      .select("*,asset_coverages!inner(coverage_reference,vendor_id)")
      .eq("organisation_id", access.organisation_id)
      .eq("asset_coverages.vendor_id", access.vendor_id)
      .order("reported_at", { ascending: false }),
    supabase
      .from("supplier_invoice_submissions")
      .select("*,purchase_orders(po_no)")
      .eq("organisation_id", access.organisation_id)
      .eq("vendor_id", access.vendor_id)
      .order("submitted_at", { ascending: false }),
    supabase
      .from("attachments")
      .select("id,object_path,file_name,mime_type")
      .eq("organisation_id", access.organisation_id)
      .eq("entity_type", "supplier_invoice_submission"),
    supabase
      .from("purchase_order_messages")
      .select("*,purchase_orders(po_no),profiles!purchase_order_messages_created_by_fkey(full_name,email)")
      .eq("organisation_id", access.organisation_id)
      .eq("vendor_id", access.vendor_id)
      .order("created_at", { ascending: true }),
    supabase
      .from("fulfilment_records")
      .select("*,commercial_instruments(source_purchase_order_id)")
      .eq("organisation_id", access.organisation_id)
      .order("created_at", { ascending: false }),
    supabase.rpc("supplier_trust_workspace", {
      p_organisation_id: access.organisation_id,
      p_vendor_id: access.vendor_id,
    } as any),
    supabase.rpc("supplier_network_workspace", {
      p_organisation_id: access.organisation_id,
      p_vendor_id: access.vendor_id,
    } as any),
    supabase.rpc("supplier_settlement_evidence", {
      p_organisation_id: access.organisation_id,
      p_vendor_id: access.vendor_id,
    } as any),
  ]);
  const error =
    rfqs.error ||
    quotes.error ||
    orders.error ||
    contracts.error ||
    messages.error ||
    campaigns.error ||
    acknowledgements.error ||
    payments.error ||
    obligations.error ||
    coverages.error ||
    claims.error ||
    invoiceSubmissions.error ||
    invoiceSubmissionAttachments.error ||
    orderMessages.error ||
    fulfilmentUpdates.error ||
    supplierTrust.error ||
    supplierNetwork.error ||
    settlementEvidence.error;
  if (error) throw new Error(error.message);
  return {
    rfqs: rfqs.data || [],
    quotes: (quotes.data || []) as unknown as Quote[],
    orders: (orders.data || []) as unknown as PurchaseOrder[],
    contracts: (contracts.data || []) as unknown as Contract[],
    messages: (messages.data || []) as SupplierMessage[],
    campaigns: (campaigns.data || []) as unknown as RequirementCampaign[],
    acknowledgements: acknowledgements.data || [],
    payments: payments.data || [],
    obligations: obligations.data || [],
    coverages: coverages.data || [],
    claims: claims.data || [],
    invoiceSubmissions: joinSupplierInvoiceEvidence(
      (invoiceSubmissions.data || []) as unknown as SupplierInvoiceSubmission[],
      (invoiceSubmissionAttachments.data || []) as SupplierInvoiceAttachment[],
    ),
    orderMessages: (orderMessages.data || []) as unknown as PurchaseOrderMessage[],
    fulfilmentUpdates: ((fulfilmentUpdates.data || []) as unknown as SupplierFulfilmentUpdate[]).filter(
      (row) => row.details?.submitted_by_supplier === true,
    ),
    supplierTrust:
      (supplierTrust.data as {
        methodology: Record<string, any> | null;
        snapshots: Record<string, any>[];
        challenges: Record<string, any>[];
      } | null) || {
        methodology: null,
        snapshots: [],
        challenges: [],
      },
    supplierNetwork:
      (supplierNetwork.data as SupplierNetworkWorkspace | null) || {
        profiles: [],
        links: [],
        claims: [],
        verifications: [],
        disputes: [],
        merge_events: [],
      },
    settlementEvidence: settlementEvidence.data || [],
  };
}

async function rpc<T>(name: string, args: Record<string, unknown>) {
  const { data, error } = await supabase.rpc(name, args as any);
  return unwrap(data, error) as T;
}

export const actions = {
  previewDataImport: async (
    organisationId: string,
    importKind: ImportKind,
    rows: Array<{
      row_number: number;
      values: Record<string, string>;
    }>,
  ) =>
    rpc<ImportPreview>(
      importKind === "control_matrix" || importKind === "historical_procurement"
        ? "preview_internal_control_import"
        : "preview_data_import",
      {
      p_organisation_id: organisationId,
      p_import_kind: importKind,
      p_rows: rows,
      },
    ),
  commitDataImport: async (
    organisationId: string,
    importKind: ImportKind,
    fileName: string,
    rows: Array<{
      row_number: number;
      values: Record<string, string>;
      action: "create" | "skip" | "update";
    }>,
    metadata?: {
      fileSha256: string;
      mappingVersion: string;
      sourceSystem: string;
      policyName?: string;
      rationale?: string;
    },
  ) => {
    if (importKind === "control_matrix") {
      if (!metadata) throw new Error("Governed import metadata is required.");
      return rpc<ImportResult>("commit_control_matrix_import", {
        p_organisation_id: organisationId,
        p_file_name: fileName,
        p_file_sha256: metadata.fileSha256,
        p_mapping_version: metadata.mappingVersion,
        p_source_system: metadata.sourceSystem,
        p_policy_id: null,
        p_policy_name: metadata.policyName,
        p_parent_version_id: null,
        p_inheritance_mode: "merge",
        p_rationale: metadata.rationale,
        p_rows: rows,
      });
    }
    if (importKind === "historical_procurement") {
      if (!metadata) throw new Error("Governed import metadata is required.");
      return rpc<ImportResult>("commit_historical_data_import", {
        p_organisation_id: organisationId,
        p_file_name: fileName,
        p_file_sha256: metadata.fileSha256,
        p_mapping_version: metadata.mappingVersion,
        p_source_system: metadata.sourceSystem,
        p_rows: rows,
      });
    }
    return rpc<ImportResult>("commit_data_import", {
      p_organisation_id: organisationId,
      p_import_kind: importKind,
      p_file_name: fileName,
      p_rows: rows,
    });
  },
  configureRequestInbox: (organisationId: string, address: string, provider: string) =>
    rpc<RequestInboxAddress>("configure_request_inbox_address", {
      p_organisation_id: organisationId,
      p_address: address,
      p_provider: provider,
    }),
  reviewRequestInboxMessage: (
    organisationId: string,
    messageId: string,
    decision: "ready" | "rejected",
    reason?: string,
  ) => rpc<RequestInboxMessage>("review_request_inbox_message", {
    p_organisation_id: organisationId,
    p_message_id: messageId,
    p_decision: decision,
    p_reason: reason || null,
  }),
  convertRequestInboxMessage: (
    organisationId: string,
    messageId: string,
    input: Record<string, unknown>,
  ) => rpc<Requisition>("convert_request_inbox_message", {
    p_organisation_id: organisationId,
    p_message_id: messageId,
    ...input,
  }),
  createVendor: (
    organisationId: string,
    input: {
      name: string;
      email: string;
      gstin?: string;
      termsDays: number;
      rating: number;
      countryCode?: string;
      defaultCurrency?: string;
      taxIdentifier?: string;
    },
  ) =>
    rpc<Vendor>("create_vendor", {
      p_organisation_id: organisationId,
      p_name: input.name,
      p_email: input.email,
      p_gstin: input.gstin || null,
      p_terms_days: input.termsDays,
      p_rating: input.rating,
      p_country_code: input.countryCode || "IN",
      p_default_currency: input.defaultCurrency || "INR",
      p_tax_identifier: input.taxIdentifier || null,
    }),
  createRequisition: (organisationId: string, input: Record<string, unknown>) =>
    rpc<Requisition>("create_requisition", {
      p_organisation_id: organisationId,
      ...input,
    }),
  updateRequisitionDraft: (
    organisationId: string,
    requisitionId: string,
    input: Record<string, unknown>,
  ) =>
    rpc<Requisition>("update_requisition_draft", {
      p_organisation_id: organisationId,
      p_requisition_id: requisitionId,
      ...input,
    }),
  setRequisitionApprovers: (
    organisationId: string,
    requisitionId: string,
    approverIds: string[],
  ) =>
    rpc<Requisition>("set_requisition_approvers", {
      p_organisation_id: organisationId,
      p_requisition_id: requisitionId,
      p_approver_ids: approverIds,
    }),
  submitRequisition: async (organisationId: string, id: string) => {
    const requisition = await rpc<Requisition>("submit_requisition", {
      p_organisation_id: organisationId,
      p_requisition_id: id,
    });
    if (requisition.status === "draft") {
      const state = requisition.guided_buying_state || "review_required";
      if (state === "alternative_selected") {
        throw new Error(
          "An existing route was selected for this need, so it will not enter new sourcing.",
        );
      }
      if (state === "exception_requested") {
        throw new Error("The guided buying exception is awaiting approver review.");
      }
      if (state === "exception_denied") {
        throw new Error(
          "The guided buying exception was denied. Refresh the checks and choose a recorded route.",
        );
      }
      throw new Error(
        "Complete the Guided Buying review before submitting this requisition.",
      );
    }
    return requisition;
  },
  decideRequisition: (
    organisationId: string,
    id: string,
    decision: string,
    comments: string,
  ) =>
    rpc<Requisition>("decide_requisition", {
      p_organisation_id: organisationId,
      p_requisition_id: id,
      p_decision: decision,
      p_comments: comments || null,
    }),
  createRfq: (
    organisationId: string,
    requisitionId: string,
    deadline: string,
    vendorIds: string[],
  ) =>
    rpc<Rfq>("create_rfq", {
      p_organisation_id: organisationId,
      p_requisition_id: requisitionId,
      p_deadline: deadline,
      p_vendor_ids: vendorIds,
    }),
  publishRfq: (organisationId: string, id: string) =>
    rpc<Rfq>("publish_rfq", {
      p_organisation_id: organisationId,
      p_rfq_id: id,
    }),
  recordQuote: (organisationId: string, input: Record<string, unknown>) =>
    rpc<Quote>("record_quote", { p_organisation_id: organisationId, ...input }),
  submitSupplierQuote: (
    organisationId: string,
    input: Record<string, unknown>,
  ) =>
    rpc<Quote>("submit_supplier_quote", {
      p_organisation_id: organisationId,
      ...input,
    }),
  reviseSupplierQuote: (
    organisationId: string,
    input: Record<string, unknown>,
  ) =>
    rpc<Quote>("revise_supplier_quote", {
      p_organisation_id: organisationId,
      ...input,
    }),
  closeRfq: (organisationId: string, id: string) =>
    rpc<Rfq>("close_rfq", { p_organisation_id: organisationId, p_rfq_id: id }),
  awardQuote: (
    organisationId: string,
    rfqId: string,
    quoteId: string,
    overrideReason: string,
  ) =>
    rpc<PurchaseOrder>("award_quote", {
      p_organisation_id: organisationId,
      p_rfq_id: rfqId,
      p_quote_id: quoteId,
      p_override_reason: overrideReason || null,
    }),
  issuePurchaseOrder: (organisationId: string, id: string) =>
    rpc<PurchaseOrder>("issue_purchase_order", {
      p_organisation_id: organisationId,
      p_purchase_order_id: id,
    }),
  configureDirectPurchasePolicy: (
    organisationId: string,
    input: Record<string, unknown>,
  ) =>
    rpc<DirectPurchasePolicy>("configure_direct_purchase_policy", {
      p_organisation_id: organisationId,
      ...input,
    }),
  createDirectPurchaseOrder: (
    organisationId: string,
    input: Record<string, unknown>,
  ) =>
    rpc<PurchaseOrder>("create_direct_purchase_order", {
      p_organisation_id: organisationId,
      ...input,
    }),
  recordReceipt: (
    organisationId: string,
    poId: string,
    notes: string,
    lines: unknown[],
  ) =>
    rpc<GoodsReceipt>("record_goods_receipt", {
      p_organisation_id: organisationId,
      p_purchase_order_id: poId,
      p_notes: notes || null,
      p_lines: lines,
    }),
  recordInvoice: (organisationId: string, input: Record<string, unknown>) =>
    rpc<Invoice>("record_invoice", {
      p_organisation_id: organisationId,
      ...input,
    }),
  matchInvoice: (organisationId: string, id: string) =>
    rpc<Invoice>("match_invoice", {
      p_organisation_id: organisationId,
      p_invoice_id: id,
    }),
  correctInvoiceRecord: (
    organisationId: string,
    id: string,
    input: {
      invoiceNo: string;
      invoiceDate: string;
      amount: number;
      taxAmount: number;
      reason: string;
    },
  ) =>
    rpc<Invoice>("correct_invoice_record", {
      p_organisation_id: organisationId,
      p_invoice_id: id,
      p_invoice_no: input.invoiceNo,
      p_invoice_date: input.invoiceDate,
      p_amount: input.amount,
      p_tax_amount: input.taxAmount,
      p_reason: input.reason,
    }),
  overrideInvoiceMatchException: (
    organisationId: string,
    id: string,
    reason: string,
  ) =>
    rpc<Invoice>("override_invoice_match_exception", {
      p_organisation_id: organisationId,
      p_invoice_id: id,
      p_reason: reason,
    }),
  approveInvoice: (organisationId: string, id: string) =>
    rpc<Invoice>("approve_invoice", {
      p_organisation_id: organisationId,
      p_invoice_id: id,
    }),
  decideSupplierInvoiceSubmission: (
    organisationId: string,
    id: string,
    decision: string,
    reason: string,
  ) =>
    rpc<SupplierInvoiceSubmission>("decide_supplier_invoice_submission", {
      p_organisation_id: organisationId,
      p_submission_id: id,
      p_decision: decision,
      p_reason: reason,
    }),
  postPurchaseOrderMessage: (
    organisationId: string,
    purchaseOrderId: string,
    body: string,
  ) =>
    rpc<PurchaseOrderMessage>("post_purchase_order_message", {
      p_organisation_id: organisationId,
      p_purchase_order_id: purchaseOrderId,
      p_body: body,
    }),
  submitSupplierFulfilmentUpdate: (
    organisationId: string,
    purchaseOrderId: string,
    input: Record<string, unknown>,
  ) =>
    rpc<SupplierFulfilmentUpdate>("submit_supplier_fulfilment_update", {
      p_organisation_id: organisationId,
      p_purchase_order_id: purchaseOrderId,
      ...input,
    }),
  decideSupplierFulfilmentUpdate: (
    organisationId: string,
    fulfilmentId: string,
    decision: string,
    reason: string,
  ) =>
    rpc<SupplierFulfilmentUpdate>("decide_supplier_fulfilment_update", {
      p_organisation_id: organisationId,
      p_fulfilment_id: fulfilmentId,
      p_decision: decision,
      p_reason: reason,
    }),
  inviteUser: async (
    organisationId: string,
    input: { email: string; fullName: string; role: string; reason: string },
  ) => {
    const { data, error } = await supabase.functions.invoke("invite-user", {
      body: { action: "invite", organisationId, ...input },
    });
    return unwrap(data, error);
  },
  resendUserInvitation: async (
    organisationId: string,
    userId: string,
    reason: string,
  ) => {
    const { data, error } = await supabase.functions.invoke("invite-user", {
      body: { action: "resend", organisationId, userId, reason },
    });
    return unwrapFunction(data, error);
  },
  cancelUserInvitation: async (
    organisationId: string,
    userId: string,
    reason: string,
  ) => {
    const { data, error } = await supabase.functions.invoke("invite-user", {
      body: { action: "cancel", organisationId, userId, reason },
    });
    return unwrap(data, error);
  },
  changeMemberRole: (
    organisationId: string,
    userId: string,
    role: string,
    reason: string,
    approvalResolution?: string,
    reassignTo?: string,
  ) =>
    rpc<TeamMember>("change_organisation_member_role", {
      p_organisation_id: organisationId,
      p_target_user_id: userId,
      p_role: role,
      p_reason: reason,
      p_approval_resolution: approvalResolution || null,
      p_reassign_to: reassignTo || null,
    }),
  setMemberStatus: (
    organisationId: string,
    userId: string,
    status: "active" | "suspended",
    reason: string,
    approvalResolution?: string,
    reassignTo?: string,
  ) =>
    rpc<TeamMember>("set_organisation_member_status", {
      p_organisation_id: organisationId,
      p_target_user_id: userId,
      p_status: status,
      p_reason: reason,
      p_approval_resolution: approvalResolution || null,
      p_reassign_to: reassignTo || null,
    }),
  revokeMember: (
    organisationId: string,
    userId: string,
    reason: string,
    approvalResolution?: string,
    reassignTo?: string,
  ) =>
    rpc<string>("revoke_organisation_membership", {
      p_organisation_id: organisationId,
      p_target_user_id: userId,
      p_reason: reason,
      p_approval_resolution: approvalResolution || null,
      p_reassign_to: reassignTo || null,
    }),
  reassignPendingMemberApprovals: (
    organisationId: string,
    formerUserId: string,
    reassignTo: string,
    reason: string,
  ) =>
    rpc<number>("reassign_pending_member_approvals", {
      p_organisation_id: organisationId,
      p_former_user_id: formerUserId,
      p_reassign_to: reassignTo,
      p_reason: reason,
    }),
  inviteSupplier: async (
    organisationId: string,
    input: {
      vendorId: string;
      email: string;
      fullName: string;
      jobTitle?: string;
    },
  ) => {
    const { data, error } = await supabase.functions.invoke("invite-supplier", {
      body: { organisationId, ...input },
    });
    return unwrap(data, error);
  },
  createContract: (organisationId: string, input: Record<string, unknown>) =>
    rpc<Contract>("create_contract", {
      p_organisation_id: organisationId,
      ...input,
    }),
  addContractItem: (organisationId: string, input: Record<string, unknown>) =>
    rpc("add_contract_item", { p_organisation_id: organisationId, ...input }),
  activateContract: (organisationId: string, id: string) =>
    rpc<Contract>("activate_contract", {
      p_organisation_id: organisationId,
      p_contract_id: id,
    }),
  createReorderTemplate: (
    organisationId: string,
    input: Record<string, unknown>,
  ) =>
    rpc<ReorderTemplate>("create_reorder_template", {
      p_organisation_id: organisationId,
      ...input,
    }),
  raiseContractReorder: (organisationId: string, id: string) =>
    rpc<Requisition>("raise_contract_reorder", {
      p_organisation_id: organisationId,
      p_reorder_template_id: id,
    }),
  createInventoryItem: (
    organisationId: string,
    input: Record<string, unknown>,
  ) =>
    rpc<InventoryItem>("create_inventory_item", {
      p_organisation_id: organisationId,
      ...input,
    }),
  adjustInventory: (
    organisationId: string,
    id: string,
    quantity: number,
    notes: string,
  ) =>
    rpc<InventoryItem>("adjust_inventory", {
      p_organisation_id: organisationId,
      p_inventory_item_id: id,
      p_quantity: quantity,
      p_notes: notes,
    }),
  raiseInventoryReorder: (organisationId: string, id: string) =>
    rpc<Requisition>("raise_inventory_reorder", {
      p_organisation_id: organisationId,
      p_inventory_item_id: id,
    }),
  runReplenishmentCycle: (organisationId: string) =>
    rpc<{ inventory_reorders: number; scheduled_reorders: number; alerts_opened: number }[]>(
      "run_replenishment_cycle",
      { p_organisation_id: organisationId },
    ),
  recordInventoryIssue: (
    organisationId: string,
    id: string,
    quantity: number,
    notes: string,
  ) =>
    rpc<InventoryItem>("record_inventory_issue", {
      p_organisation_id: organisationId,
      p_inventory_item_id: id,
      p_quantity: quantity,
      p_reference_type: "manual_issue",
      p_reference_id: null,
      p_notes: notes,
    }),
  configureInventoryReplenishment: (
    organisationId: string,
    id: string,
    reorderPoint: number,
    targetStock: number,
    autoReorder: boolean,
    autoSubmit: boolean,
  ) =>
    rpc<InventoryItem>("configure_inventory_replenishment", {
      p_organisation_id: organisationId,
      p_inventory_item_id: id,
      p_reorder_point: reorderPoint,
      p_target_stock: targetStock,
      p_auto_reorder: autoReorder,
      p_auto_submit: autoSubmit,
    }),
  classifyRequisitionLine: (
    organisationId: string,
    input: Record<string, unknown>,
  ) =>
    rpc("classify_requisition_line", {
      p_organisation_id: organisationId,
      ...input,
    }),
  configureCapitalisationPolicy: (
    organisationId: string,
    input: Record<string, unknown>,
  ) =>
    rpc<CapitalisationPolicy>("configure_capitalisation_policy", {
      p_organisation_id: organisationId,
      ...input,
    }),
  commissionAssetCandidate: (
    organisationId: string,
    input: Record<string, unknown>,
  ) =>
    rpc<Asset>("commission_asset_candidate", {
      p_organisation_id: organisationId,
      ...input,
    }),
  transferAssetCustody: (
    organisationId: string,
    input: Record<string, unknown>,
  ) =>
    rpc<Asset>("transfer_asset_custody", {
      p_organisation_id: organisationId,
      ...input,
    }),
  acknowledgeAssetCustody: (
    organisationId: string,
    assetId: string,
    note: string,
  ) =>
    rpc("acknowledge_asset_custody", {
      p_organisation_id: organisationId,
      p_asset_id: assetId,
      p_note: note,
    }),
  createRequirementCampaign: (
    organisationId: string,
    input: Record<string, unknown>,
  ) =>
    rpc<RequirementCampaign>("create_requirement_campaign", {
      p_organisation_id: organisationId,
      ...input,
    }),
  publishRequirementCampaign: (organisationId: string, id: string) =>
    rpc<RequirementCampaign>("publish_requirement_campaign", {
      p_organisation_id: organisationId,
      p_campaign_id: id,
    }),
  markSupplierMessageRead: (organisationId: string, id: string) =>
    rpc<SupplierMessage>("mark_supplier_message_read", {
      p_organisation_id: organisationId,
      p_message_id: id,
    }),
  createBusinessUnit: (
    organisationId: string,
    input: Record<string, unknown>,
  ) =>
    rpc<BusinessUnit>("create_business_unit", {
      p_organisation_id: organisationId,
      ...input,
    }),
  assignResponsibility: (
    organisationId: string,
    input: Record<string, unknown>,
  ) =>
    rpc("assign_responsibility", {
      p_organisation_id: organisationId,
      ...input,
    }),
  createBudget: (organisationId: string, input: Record<string, unknown>) =>
    rpc<Budget>("create_budget", {
      p_organisation_id: organisationId,
      ...input,
    }),
  createBudgetedRequisition: (
    organisationId: string,
    input: Record<string, unknown>,
  ) =>
    rpc<Requisition>("create_budgeted_requisition", {
      p_organisation_id: organisationId,
      ...input,
    }),
  linkOrganisations: (
    parentOrganisationId: string,
    childOrganisationId: string,
    relationship: string,
    sharingLevel: string,
  ) =>
    rpc<OrganisationLink>("link_organisations", {
      p_parent_organisation_id: parentOrganisationId,
      p_child_organisation_id: childOrganisationId,
      p_relationship: relationship,
      p_sharing_level: sharingLevel,
    }),
  acknowledgePurchaseOrder: (
    organisationId: string,
    purchaseOrderId: string,
    response: string,
    reason = "",
    proposedDelivery: Record<string, unknown> | null = null,
  ) =>
    rpc("acknowledge_purchase_order", {
      p_organisation_id: organisationId,
      p_purchase_order_id: purchaseOrderId,
      p_response: response,
      p_reason: reason || null,
      p_proposed_delivery: proposedDelivery,
    }),
  proposePayment: (
    organisationId: string,
    paymentId: string,
    scheduledDate: string,
  ) =>
    rpc("propose_payment", {
      p_organisation_id: organisationId,
      p_payment_id: paymentId,
      p_scheduled_date: scheduledDate,
    }),
  approvePayment: (organisationId: string, paymentId: string) =>
    rpc("approve_payment", {
      p_organisation_id: organisationId,
      p_payment_id: paymentId,
    }),
  createCommercialPaymentSchedule: (
    organisationId: string,
    definition: Record<string, unknown>,
  ) =>
    rpc("create_commercial_payment_schedule", {
      p_organisation_id: organisationId,
      p_definition: definition,
    }),
  submitCommercialPaymentSchedule: (
    organisationId: string,
    scheduleId: string,
  ) =>
    rpc("submit_commercial_payment_schedule", {
      p_organisation_id: organisationId,
      p_schedule_id: scheduleId,
    }),
  approveCommercialPaymentSchedule: (
    organisationId: string,
    scheduleId: string,
    decision: "approved" | "rejected",
    reason: string,
  ) =>
    rpc("approve_commercial_payment_schedule", {
      p_organisation_id: organisationId,
      p_schedule_id: scheduleId,
      p_decision: decision,
      p_reason: reason,
    }),
  recordPaymentInitiated: (
    organisationId: string,
    paymentId: string,
    externalReference: string,
  ) =>
    rpc("record_payment_initiated", {
      p_organisation_id: organisationId,
      p_payment_id: paymentId,
      p_external_reference: externalReference,
      p_destination_token_reference: null,
    }),
  recordPaymentCompleted: (
    organisationId: string,
    paymentId: string,
    externalReference: string,
  ) =>
    rpc("record_payment_completed", {
      p_organisation_id: organisationId,
      p_payment_id: paymentId,
      p_external_reference: externalReference,
    }),
  reconcilePayment: (
    organisationId: string,
    paymentId: string,
    sourceSystem: string,
    sourceReference: string,
    bankReference: string,
    matchedAmount: number,
  ) =>
    rpc("reconcile_payment", {
      p_organisation_id: organisationId,
      p_payment_id: paymentId,
      p_source_system: sourceSystem,
      p_source_transaction_reference: sourceReference || null,
      p_bank_reference: bankReference || null,
      p_matched_amount: matchedAmount,
    }),
  registerBankStatementImport: (
    organisationId: string,
    input: Record<string, unknown>,
  ) =>
    rpc<BankStatementImport>("register_bank_statement_import", {
      p_organisation_id: organisationId,
      ...input,
    }),
  confirmBankStatementLines: (
    organisationId: string,
    statementId: string,
    lines: Record<string, unknown>[],
  ) =>
    rpc<BankStatementImport>("confirm_bank_statement_lines", {
      p_organisation_id: organisationId,
      p_statement_id: statementId,
      p_lines: lines,
    }),
  matchBankStatementLine: (
    organisationId: string,
    lineId: string,
    paymentId: string,
    matchedAmount: number,
    reason: string,
  ) =>
    rpc<BankStatementLine>("match_bank_statement_line", {
      p_organisation_id: organisationId,
      p_line_id: lineId,
      p_payment_id: paymentId,
      p_matched_amount: matchedAmount,
      p_reason: reason,
    }),
  ignoreBankStatementLine: (
    organisationId: string,
    lineId: string,
    reason: string,
  ) =>
    rpc<BankStatementLine>("ignore_bank_statement_line", {
      p_organisation_id: organisationId,
      p_line_id: lineId,
      p_reason: reason,
    }),
  closeProcurementCase: (organisationId: string, caseId: string) =>
    rpc("close_procurement_case", {
      p_organisation_id: organisationId,
      p_case_id: caseId,
    }),
};

export async function uploadAttachment(
  organisationId: string,
  entityType: string,
  entityId: string,
  file: File,
  options: {
    sha256?: string;
    isOriginalEvidence?: boolean;
    supersedesAttachmentId?: string | null;
  } = {},
) {
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "-");
  const objectPath = `${organisationId}/${entityType}/${entityId}/${crypto.randomUUID()}-${safeName}`;
  const { error: uploadError } = await supabase.storage
    .from("raqoz-documents")
    .upload(objectPath, file, { upsert: false });
  if (uploadError) throw new Error(uploadError.message);
  const { data: auth } = await supabase.auth.getUser();
  const { data, error } = await supabase
    .from("attachments")
    .insert({
      organisation_id: organisationId,
      entity_type: entityType,
      entity_id: entityId,
      object_path: objectPath,
      file_name: file.name,
      mime_type: file.type || "application/octet-stream",
      byte_size: file.size,
      uploaded_by: auth.user?.id,
      sha256: options.sha256 || null,
      is_original_evidence: Boolean(options.isOriginalEvidence),
      supersedes_attachment_id: options.supersedesAttachmentId || null,
    })
    .select()
    .single();
  if (error) {
    await supabase.storage.from("raqoz-documents").remove([objectPath]);
    throw new Error(error.message);
  }
  return data;
}

export async function commercialSettlementWorkspace(organisationId: string) {
  return rpc<any>("commercial_settlement_workspace", {
    p_organisation_id: organisationId,
  });
}

export async function uploadSettlementEvidence(
  organisationId: string,
  target: { paymentId?: string | null; scheduleId?: string | null },
  input: {
    evidenceType: string;
    externalReference: string;
    settlementDate: string;
    amount: number;
    currency: string;
    supplierVisible: boolean;
    note?: string;
    file?: File | null;
  },
) {
  const targetId = target.paymentId || target.scheduleId;
  if (!targetId || Boolean(target.paymentId) === Boolean(target.scheduleId))
    throw new Error("Choose exactly one payment or commercial schedule.");
  let attachment: Record<string, any> | null = null;
  if (input.file) {
    const allowed = new Set(["application/pdf", "image/png", "image/jpeg"]);
    if (!allowed.has(input.file.type))
      throw new Error("Upload a PDF, PNG or JPEG settlement document.");
    const sha256 = await sha256File(input.file);
    attachment = await uploadAttachment(
      organisationId,
      "payment_evidence",
      targetId,
      input.file,
      { sha256, isOriginalEvidence: true },
    );
  }
  try {
    return await rpc<any>("record_settlement_evidence", {
      p_organisation_id: organisationId,
      p_payment_id: target.paymentId || null,
      p_schedule_id: target.scheduleId || null,
      p_attachment_id: attachment?.id || null,
      p_evidence_type: input.evidenceType,
      p_external_reference: input.externalReference,
      p_settlement_date: input.settlementDate,
      p_amount: input.amount,
      p_currency: input.currency.toUpperCase(),
      p_supplier_visible: input.supplierVisible,
      p_note: input.note || null,
    });
  } catch (error) {
    if (attachment) {
      await supabase.from("attachments").delete().eq("id", attachment.id);
      await supabase.storage.from("raqoz-documents").remove([attachment.object_path]);
    }
    throw error;
  }
}

export async function settlementEvidenceUrl(objectPath: string) {
  const { data, error } = await supabase.storage
    .from("raqoz-documents")
    .createSignedUrl(objectPath, 300);
  if (error || !data?.signedUrl)
    throw new Error(error?.message || "Settlement evidence is unavailable.");
  return data.signedUrl;
}

export async function supplierSettlementEvidence(
  organisationId: string,
  vendorId: string,
) {
  return rpc<any[]>("supplier_settlement_evidence", {
    p_organisation_id: organisationId,
    p_vendor_id: vendorId,
  });
}

export async function sha256File(file: File) {
  const digest = await crypto.subtle.digest("SHA-256", await file.arrayBuffer());
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

export async function bankStatementWorkspace(organisationId: string) {
  const [{ data: statements, error: statementError }, { data: lines, error: lineError }] =
    await Promise.all([
      supabase
        .from("bank_statement_imports")
        .select("*")
        .eq("organisation_id", organisationId)
        .order("created_at", { ascending: false }),
      supabase
        .from("bank_statement_lines")
        .select("*")
        .eq("organisation_id", organisationId)
        .order("transaction_date", { ascending: false })
        .order("line_number"),
    ]);
  if (statementError || lineError)
    throw new Error(statementError?.message || lineError?.message);
  const statementRows = (statements || []) as BankStatementImport[];
  let attachmentRows: Record<string, any>[] = [];
  if (statementRows.length) {
    const { data, error } = await supabase
      .from("attachments")
      .select("entity_id,object_path,file_name,mime_type,sha256")
      .eq("organisation_id", organisationId)
      .eq("entity_type", "bank_statement_import")
      .in("entity_id", statementRows.map((row) => row.id));
    if (error) throw new Error(error.message);
    attachmentRows = data || [];
  }
  return {
    statements: statementRows.map((row) => ({
      ...row,
      attachments:
        attachmentRows.find((attachment) => attachment.entity_id === row.id) ||
        null,
    })),
    lines: (lines || []) as BankStatementLine[],
  };
}

export async function importBankStatement(
  organisationId: string,
  file: File,
  input: {
    sourceFormat: "csv" | "xlsx" | "pdf";
    accountLabel: string;
    periodStart?: string;
    periodEnd?: string;
    currency: string;
    parsingNote?: string;
    lines: Record<string, unknown>[];
  },
) {
  const allowed = new Set(["csv", "xlsx", "pdf"]);
  if (!allowed.has(input.sourceFormat))
    throw new Error("Upload a CSV, XLSX or PDF bank statement.");
  if (file.size <= 0 || file.size > 26214400)
    throw new Error("Bank statement evidence must be between 1 byte and 25 MB.");
  if (!input.lines.length)
    throw new Error("Review or manually add at least one statement line.");
  const statementId = crypto.randomUUID();
  const sha256 = await sha256File(file);
  const attachment = await uploadAttachment(
    organisationId,
    "bank_statement_import",
    statementId,
    file,
    { sha256, isOriginalEvidence: true },
  );
  await actions.registerBankStatementImport(organisationId, {
    p_statement_id: statementId,
    p_attachment_id: attachment.id,
    p_source_format: input.sourceFormat,
    p_account_label: input.accountLabel,
    p_period_start: input.periodStart || null,
    p_period_end: input.periodEnd || null,
    p_currency: input.currency.toUpperCase(),
    p_sha256: sha256,
    p_parsing_note: input.parsingNote || null,
  });
  return actions.confirmBankStatementLines(
    organisationId,
    statementId,
    input.lines,
  );
}

export async function bankStatementAttachmentUrl(objectPath: string) {
  const { data, error } = await supabase.storage
    .from("raqoz-documents")
    .createSignedUrl(objectPath, 300);
  if (error || !data?.signedUrl)
    throw new Error(error?.message || "Bank statement evidence is unavailable.");
  return data.signedUrl;
}

export async function uploadSupplierInvoice(
  access: SupplierAccess,
  purchaseOrderId: string,
  file: File,
  invoice: {
    invoiceNo: string;
    invoiceDate: string;
    subtotal: number;
    taxAmount: number;
    currency: string;
  },
) {
  const allowedTypes = new Set(["application/pdf", "image/png", "image/jpeg"]);
  if (!allowedTypes.has(file.type))
    throw new Error("Upload a PDF, PNG or JPEG invoice evidence file.");
  if (file.size <= 0 || file.size > 26214400)
    throw new Error("Invoice evidence must be between 1 byte and 25 MB.");
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "-");
  const objectPath = `${access.organisation_id}/supplier-inbox/${access.vendor_id}/${crypto.randomUUID()}-${safeName}`;
  const sha256 = await sha256File(file);
  const { error: uploadError } = await supabase.storage
    .from("raqoz-documents")
    .upload(objectPath, file, { upsert: false });
  if (uploadError) throw new Error(uploadError.message);
  try {
    return await rpc<SupplierInvoiceSubmission>("submit_supplier_invoice", {
      p_organisation_id: access.organisation_id,
      p_vendor_id: access.vendor_id,
      p_purchase_order_id: purchaseOrderId,
      p_invoice_no: invoice.invoiceNo,
      p_invoice_date: invoice.invoiceDate,
      p_subtotal: invoice.subtotal,
      p_tax_amount: invoice.taxAmount,
      p_currency: invoice.currency.toUpperCase(),
      p_object_path: objectPath,
      p_file_name: file.name,
      p_mime_type: file.type,
      p_byte_size: file.size,
      p_sha256: sha256,
      p_extraction_state: "provider_not_configured",
      p_extracted_fields: {},
      p_field_confidence: {},
    });
  } catch (error) {
    await supabase.storage.from("raqoz-documents").remove([objectPath]);
    throw error;
  }
}

export async function supplierInvoiceEvidenceUrl(objectPath: string) {
  const { data, error } = await supabase.storage
    .from("raqoz-documents")
    .createSignedUrl(objectPath, 300);
  if (error) throw new Error(error.message);
  return data.signedUrl;
}

export async function uploadQuotationEvidence(
  organisationId: string,
  rfqId: string,
  file: File,
  supersedesAttachmentId?: string | null,
) {
  const sha256 = await sha256File(file);
  const attachment = await uploadAttachment(
    organisationId,
    "rfq",
    rfqId,
    file,
    {
      sha256,
      isOriginalEvidence: true,
      supersedesAttachmentId,
    },
  );
  return { attachment, sha256 };
}

export async function listQuotationEvidence(
  organisationId: string,
  rfqId: string,
) {
  const { data, error } = await supabase
    .from("quotation_evidence_workspace")
    .select("*")
    .eq("organisation_id", organisationId)
    .eq("rfq_id", rfqId)
    .order("created_at", { ascending: false });
  return unwrap(data, error) as unknown as QuotationEvidence[];
}

export async function quotationEvidenceWorkspace(
  organisationId: string,
  rfqId: string,
) {
  const [rfq, evidence] = await Promise.all([
    rfqDetail(organisationId, rfqId),
    listQuotationEvidence(organisationId, rfqId),
  ]);
  return { ...rfq, evidence };
}

export async function captureReceivedQuotation(
  organisationId: string,
  input: Record<string, unknown>,
) {
  return rpc<QuotationEvidence>("capture_received_quotation", {
    p_organisation_id: organisationId,
    ...input,
  });
}

export async function decideReceivedQuotation(
  organisationId: string,
  evidenceId: string,
  decision: string,
  reason: string,
) {
  return rpc<QuotationEvidence>("decide_received_quotation", {
    p_organisation_id: organisationId,
    p_evidence_id: evidenceId,
    p_decision: decision,
    p_reason: reason,
  });
}

export async function quotationEvidenceUrl(objectPath: string) {
  const { data, error } = await supabase.storage
    .from("raqoz-documents")
    .createSignedUrl(objectPath, 300);
  if (error) throw new Error(error.message);
  return data.signedUrl;
}

export async function getPublicTenantBranding(
  tenantHint: string,
): Promise<TenantBranding | null> {
  if (!tenantHint) return null;
  const { data, error } = await supabase.rpc(
    "get_public_organisation_branding",
    { p_tenant_hint: tenantHint } as any,
  );
  if (error) return null;
  const row = (Array.isArray(data) ? data[0] : data) as
    | Record<string, unknown>
    | undefined;
  if (!row?.organisation_name) return null;
  const logoPath =
    typeof row.logo_path === "string" && row.logo_path ? row.logo_path : null;
  const logoUrl = logoPath
    ? supabase.storage.from("raqoz-brand-assets").getPublicUrl(logoPath).data
        .publicUrl
    : null;
  return {
    organisationName: String(row.organisation_name),
    logoPath,
    logoUrl,
    primaryColor: safeBrandColour(
      row.primary_color,
      DEFAULT_TENANT_BRANDING.primaryColor,
    ),
    accentColor: safeBrandColour(
      row.accent_color,
      DEFAULT_TENANT_BRANDING.accentColor,
    ),
  };
}

export async function saveOrganisationBranding(
  organisationId: string,
  input: {
    primaryColor: string;
    accentColor: string;
    currentLogoPath: string | null;
    logoFile?: File | null;
    removeLogo?: boolean;
  },
) {
  let logoPath = input.removeLogo ? null : input.currentLogoPath;
  let uploadedPath: string | null = null;
  if (input.logoFile) {
    const extensions: Record<string, string> = {
      "image/png": "png",
      "image/jpeg": "jpg",
      "image/webp": "webp",
    };
    const extension = extensions[input.logoFile.type];
    if (!extension) throw new Error("Use a PNG, JPEG or WebP logo.");
    if (input.logoFile.size > 2 * 1024 * 1024)
      throw new Error("The logo must be 2 MB or smaller.");
    uploadedPath = `${organisationId}/branding/${crypto.randomUUID()}.${extension}`;
    const { error: uploadError } = await supabase.storage
      .from("raqoz-brand-assets")
      .upload(uploadedPath, input.logoFile, {
        upsert: false,
        contentType: input.logoFile.type,
      });
    if (uploadError) throw new Error(uploadError.message);
    logoPath = uploadedPath;
  }

  const { data, error } = await supabase.rpc("update_organisation_branding", {
    p_organisation_id: organisationId,
    p_logo_path: logoPath,
    p_primary_color: input.primaryColor,
    p_accent_color: input.accentColor,
  } as any);
  if (error) {
    if (uploadedPath)
      await supabase.storage.from("raqoz-brand-assets").remove([uploadedPath]);
    throw new Error(error.message);
  }
  if (input.currentLogoPath && input.currentLogoPath !== logoPath) {
    await supabase.storage
      .from("raqoz-brand-assets")
      .remove([input.currentLogoPath]);
  }
  return (Array.isArray(data) ? data[0] : data) as Organisation;
}

export async function transactionWorkspace(organisationId: string) {
  const [workspace, adjustments] = await Promise.all([
    rpc<{ cases: any[]; payments: any[]; matches: any[]; timeline: any[] }>(
      "transaction_workspace",
      { p_organisation_id: organisationId },
    ),
    supabase
      .from("commercial_adjustments")
      .select("*")
      .eq("organisation_id", organisationId)
      .order("created_at", { ascending: false }),
  ]);
  if (adjustments.error) throw new Error(adjustments.error.message);
  return { ...workspace, adjustments: adjustments.data || [] };
}

export async function procurementAuditWorkspace(organisationId: string) {
  const [
    workspace,
    requisitions,
    approvals,
    rfqs,
    purchaseOrders,
    receipts,
    fulfilment,
    invoices,
    audit,
  ] = await Promise.all([
    transactionWorkspace(organisationId),
    supabase
      .from("requisitions")
      .select(
        "id,request_no,title,status,estimated_value,created_by,created_at,updated_at,actor:profiles!requisitions_created_by_fkey(full_name,email)",
      )
      .eq("organisation_id", organisationId)
      .order("created_at", { ascending: false }),
    supabase
      .from("requisition_approvals")
      .select(
        "id,requisition_id,approver_id,decision,comments,created_at,actor:profiles!requisition_approvals_approver_id_fkey(full_name,email)",
      )
      .eq("organisation_id", organisationId)
      .order("created_at"),
    supabase
      .from("rfqs")
      .select(
        "id,rfq_no,requisition_id,title,status,created_by,created_at,updated_at,actor:profiles!rfqs_created_by_fkey(full_name,email)",
      )
      .eq("organisation_id", organisationId)
      .order("created_at"),
    supabase
      .from("purchase_orders")
      .select(
        "id,po_no,rfq_id,requisition_id,vendor_id,status,total,currency,procurement_route,source_channel,route_reason,external_reference,created_by,issued_at,created_at,updated_at,actor:profiles!purchase_orders_created_by_fkey(full_name,email),vendors(name)",
      )
      .eq("organisation_id", organisationId)
      .order("created_at"),
    supabase
      .from("goods_receipts")
      .select(
        "id,grn_no,purchase_order_id,received_by,notes,received_at,actor:profiles!goods_receipts_received_by_fkey(full_name,email)",
      )
      .eq("organisation_id", organisationId)
      .order("received_at"),
    supabase
      .from("fulfilment_records")
      .select(
        "id,procurement_case_id,instrument_id,kind,state,reference_no,occurred_at,details,recorded_by,created_at,actor:profiles!fulfilment_records_recorded_by_fkey(full_name,email)",
      )
      .eq("organisation_id", organisationId)
      .order("created_at"),
    supabase
      .from("invoices")
      .select(
        "id,invoice_no,purchase_order_id,amount,tax_amount,status,created_by,created_at,updated_at,actor:profiles!invoices_created_by_fkey(full_name,email)",
      )
      .eq("organisation_id", organisationId)
      .order("created_at"),
    listAudit(organisationId, 500),
  ]);
  const queryResults = [
    requisitions,
    approvals,
    rfqs,
    purchaseOrders,
    receipts,
    fulfilment,
    invoices,
  ];
  const failed = queryResults.find((result) => result.error);
  if (failed?.error) throw new Error(failed.error.message);
  return {
    ...workspace,
    requisitions: requisitions.data || [],
    approvals: approvals.data || [],
    rfqs: rfqs.data || [],
    purchaseOrders: purchaseOrders.data || [],
    receipts: receipts.data || [],
    fulfilment: fulfilment.data || [],
    invoices: invoices.data || [],
    audit,
  };
}

export async function recordFulfilmentEvent(
  organisationId: string,
  purchaseOrderId: string,
  input: {
    kind: string;
    occurredAt: string;
    reference?: string;
    performedBy: string;
    note: string;
  },
) {
  return rpc<Record<string, unknown>>("record_fulfilment_event", {
    p_organisation_id: organisationId,
    p_purchase_order_id: purchaseOrderId,
    p_kind: input.kind,
    p_occurred_at: input.occurredAt,
    p_reference: input.reference || null,
    p_details: {
      performed_by: input.performedBy.trim(),
      note: input.note.trim(),
    },
  });
}

export async function transactionAuditPack(
  organisationId: string,
  caseId: string,
) {
  return rpc<Record<string, unknown>>("transaction_audit_pack", {
    p_organisation_id: organisationId,
    p_case_id: caseId,
  });
}

export type ProcurementDocumentSnapshot = {
  id: string;
  organisation_id: string;
  procurement_case_id: string;
  document_kind: "file_note" | "audit_pack";
  version: number;
  payload: Record<string, any>;
  sha256: string;
  verification_code: string;
  generated_by: string;
  generated_at: string;
};

export async function createProcurementDocumentSnapshot(
  organisationId: string,
  caseId: string,
  documentKind: "file_note" | "audit_pack",
) {
  return rpc<ProcurementDocumentSnapshot>(
    "create_procurement_document_snapshot",
    {
      p_organisation_id: organisationId,
      p_case_id: caseId,
      p_document_kind: documentKind,
    },
  );
}

export async function listProcurementDocumentSnapshots(
  organisationId: string,
  caseId: string,
) {
  const { data, error } = await supabase
    .from("procurement_document_snapshots")
    .select(
      "id,organisation_id,procurement_case_id,document_kind,version,payload,sha256,verification_code,generated_by,generated_at",
    )
    .eq("organisation_id", organisationId)
    .eq("procurement_case_id", caseId)
    .order("generated_at", { ascending: false });
  return unwrap(data, error) as ProcurementDocumentSnapshot[];
}

export async function verifyProcurementDocument(verificationCode: string) {
  const { data, error } = await supabase.rpc("verify_procurement_document", {
    p_verification_code: verificationCode,
  });
  if (error) throw new Error(error.message);
  return (data?.[0] || null) as null | {
    document_id: string;
    document_kind: string;
    version: number;
    sha256: string;
    generated_at: string;
    organisation_name: string;
    case_reference: string;
    valid: boolean;
  };
}

export async function getPublicContentByPath(routePath: string) {
  const { data, error } = await supabase.rpc("get_public_content_by_path", {
    p_route_path: routePath,
  });
  if (error) throw new Error(error.message);
  return (data || null) as null | {
    title: string;
    excerpt: string;
    body: { type?: string; text?: string }[];
    content_type: string;
    category?: string | null;
    tags?: string[];
    published_at?: string | null;
    updated_at: string;
    hero_image_url?: string | null;
  };
}

export async function listContentEntries() {
  const { data, error } = await supabase
    .from("content_entries")
    .select(
      "*,author:content_authors!content_entries_author_id_fkey(display_name),reviewer:content_authors!content_entries_reviewer_id_fkey(display_name)",
    )
    .order("updated_at", { ascending: false });
  return unwrap(data, error) as any[];
}

export async function listContentAuthors() {
  const { data, error } = await supabase
    .from("content_authors")
    .select("*")
    .eq("active", true)
    .order("display_name");
  return unwrap(data, error) as any[];
}

export async function createContentAuthor(
  displayName: string,
  roleTitle: string,
) {
  const { data, error } = await supabase
    .from("content_authors")
    .insert({
      display_name: displayName.trim(),
      role_title: roleTitle.trim() || null,
    })
    .select()
    .single();
  return unwrap(data, error);
}

export async function saveContentEntry(input: Record<string, any>) {
  const { data: auth, error: authError } = await supabase.auth.getUser();
  if (authError || !auth.user) throw new Error("Authentication required");
  const publishing = ["published", "updated"].includes(String(input.status));
  const payload = {
    content_type: input.content_type,
    title: String(input.title).trim(),
    slug: String(input.slug).trim(),
    route_path: String(input.route_path).trim(),
    excerpt: String(input.excerpt).trim(),
    author_id: input.author_id || null,
    reviewer_id: input.reviewer_id || null,
    hero_image_url: input.hero_image_url || null,
    body: String(input.body_text || "")
      .split(/\n{2,}/)
      .map((text: string) => ({ type: "paragraph", text: text.trim() }))
      .filter((block: any) => block.text),
    category: input.category || null,
    tags: String(input.tags || "")
      .split(",")
      .map((tag: string) => tag.trim())
      .filter(Boolean),
    canonical_url: input.canonical_url || null,
    seo_title: String(input.seo_title).trim(),
    meta_description: String(input.meta_description).trim(),
    robots: input.robots || "noindex_nofollow",
    structured_data_type: input.structured_data_type || "article",
    availability: publishing ? "available" : input.availability || "planned",
    status: input.status || "draft",
    published_at: publishing
      ? input.published_at || new Date().toISOString()
      : null,
    updated_by: auth.user.id,
  };
  if (input.id) {
    const { data, error } = await supabase
      .from("content_entries")
      .update(payload)
      .eq("id", input.id)
      .select()
      .single();
    return unwrap(data, error);
  }
  const { data, error } = await supabase
    .from("content_entries")
    .insert({ ...payload, created_by: auth.user.id })
    .select()
    .single();
  return unwrap(data, error);
}

export async function uploadContentMedia(file: File) {
  if (
    !["image/png", "image/jpeg", "image/webp", "image/avif"].includes(file.type)
  )
    throw new Error("Use PNG, JPEG, WebP or AVIF.");
  if (file.size > 5 * 1024 * 1024)
    throw new Error("Content media must be 5 MB or smaller.");
  const extension =
    file.name
      .split(".")
      .pop()
      ?.replace(/[^a-z0-9]/gi, "")
      .toLowerCase() || "bin";
  const objectPath = "content/" + crypto.randomUUID() + "." + extension;
  const { error } = await supabase.storage
    .from("raqoz-content-media")
    .upload(objectPath, file, { upsert: false, contentType: file.type });
  if (error) throw new Error(error.message);
  return supabase.storage.from("raqoz-content-media").getPublicUrl(objectPath)
    .data.publicUrl;
}

export async function loadReportingSetup(organisationId: string) {
  const [
    { data: rules, error: rulesError },
    { data: profile, error: profileError },
    { data: purchaseOrders, error: poError },
  ] = await Promise.all([
    supabase.from("country_reporting_rules").select("*").order("country_name"),
    supabase
      .from("organisation_reporting_profiles")
      .select("*")
      .eq("organisation_id", organisationId)
      .maybeSingle(),
    supabase
      .from("purchase_orders")
      .select(
        "id,po_no,currency,vendor_id,vendors(name,country_code,tax_identifier)",
      )
      .eq("organisation_id", organisationId)
      .order("created_at", { ascending: false }),
  ]);
  if (rulesError) throw new Error(rulesError.message);
  if (profileError) throw new Error(profileError.message);
  if (poError) throw new Error(poError.message);
  return { rules: rules || [], profile, purchaseOrders: purchaseOrders || [] };
}

export async function generateComplianceReport(
  organisationId: string,
  countryCode: string,
  periodStart: string,
  periodEnd: string,
  cadence: string,
) {
  return rpc<Record<string, any>>("generate_compliance_report", {
    p_organisation_id: organisationId,
    p_country_code: countryCode,
    p_period_start: periodStart,
    p_period_end: periodEnd,
    p_cadence: cadence,
  });
}

export async function saveReportingProfile(
  organisationId: string,
  input: {
    countryCode: string;
    taxRegistrationNumber: string;
    fiscalYearStartMonth: number;
    reportingCurrency: string;
  },
) {
  return rpc("save_reporting_profile", {
    p_organisation_id: organisationId,
    p_country_code: input.countryCode,
    p_tax_registration_number: input.taxRegistrationNumber,
    p_fiscal_year_start_month: input.fiscalYearStartMonth,
    p_reporting_currency: input.reportingCurrency,
  });
}

export async function saveTradeComplianceDetail(
  organisationId: string,
  input: Record<string, any>,
) {
  return rpc("save_trade_compliance_detail", {
    p_organisation_id: organisationId,
    p_purchase_order_id: input.purchaseOrderId,
    p_invoice_id: input.invoiceId || null,
    p_direction: input.direction,
    p_incoterm: input.incoterm || null,
    p_country_of_origin: input.countryOfOrigin || null,
    p_country_of_destination: input.countryOfDestination || null,
    p_customs_document_type: input.customsDocumentType || null,
    p_customs_document_number: input.customsDocumentNumber || null,
    p_customs_document_date: input.customsDocumentDate || null,
    p_hs_code_summary: input.hsCodeSummary || null,
    p_assessable_value:
      input.assessableValue === "" ? null : Number(input.assessableValue),
    p_freight_amount:
      input.freightAmount === "" ? null : Number(input.freightAmount),
    p_insurance_amount:
      input.insuranceAmount === "" ? null : Number(input.insuranceAmount),
    p_customs_duty_amount:
      input.customsDutyAmount === "" ? null : Number(input.customsDutyAmount),
    p_import_tax_amount:
      input.importTaxAmount === "" ? null : Number(input.importTaxAmount),
    p_export_reference: input.exportReference || null,
    p_notes: input.notes || null,
  });
}

export async function selfServiceCreateOrganisation(input: {
  name: string;
  legalName?: string;
  countryCode: string;
  taxIdentifier?: string;
}) {
  return rpc<Organisation>("self_service_create_organisation", {
    p_name: input.name,
    p_legal_name: input.legalName || null,
    p_country_code: input.countryCode,
    p_tax_identifier: input.taxIdentifier || null,
  });
}

export async function organisationActivationWorkspace(organisationId: string) {
  const [locations, units, rules, team, vendors, inventory, budgets, requisitions] =
    await Promise.all([
      listLocations(organisationId),
      listBusinessUnits(organisationId),
      listApprovalRules(organisationId),
      listTeam(organisationId),
      listVendors(organisationId),
      listInventory(organisationId),
      listBudgets(organisationId),
      listRequisitions(organisationId),
    ]);
  return {
    locations,
    units,
    rules,
    team,
    vendors,
    inventory,
    budgets,
    requisitions,
    counts: {
      locations: locations.length,
      units: units.length,
      activeMembers: team.filter((member) => member.status === "active").length,
      activeApprovers: team.filter(
        (member) => member.status === "active" && ["approver", "tenant_admin"].includes(member.role),
      ).length,
      rules: rules.filter((rule) => rule.active).length,
      vendors: vendors.filter((vendor) => vendor.active).length,
      catalogueItems: inventory.filter((item) => item.active).length,
      budgets: budgets.filter((budget) => budget.status === "active").length,
      requisitions: requisitions.length,
    },
  };
}

// RAQOZ v3.1 value assurance and post-payment obligation operations.
export async function valueAssuranceWorkspace(organisationId: string) {
  const [assurance, operations] = await Promise.all([
    rpc<any>("value_assurance_workspace", {
      p_organisation_id: organisationId,
    }),
    rpc<any>("recurring_coverage_operations_workspace", {
      p_organisation_id: organisationId,
    }),
  ]);
  return { ...assurance, ...operations };
}
export async function createRecurringCommitment(
  organisationId: string,
  definition: Record<string, unknown>,
) {
  return rpc<any>("create_recurring_commitment", {
    p_organisation_id: organisationId,
    p_definition: definition,
  });
}
export async function approveRecurringCommitment(
  organisationId: string,
  commitmentId: string,
  reason = "",
) {
  return rpc<any>("approve_recurring_commitment", {
    p_organisation_id: organisationId,
    p_commitment_id: commitmentId,
    p_reason: reason || null,
  });
}
export async function activateRecurringCommitment(
  organisationId: string,
  commitmentId: string,
) {
  return rpc<any>("activate_recurring_commitment", {
    p_organisation_id: organisationId,
    p_commitment_id: commitmentId,
  });
}
export async function prepareDueCommitmentReleases(
  organisationId: string,
  asOf: string,
) {
  return rpc<Record<string, any>>("prepare_due_commitment_releases", {
    p_organisation_id: organisationId,
    p_as_of: asOf,
  });
}
export async function prepareRecurringPaymentSchedules(
  organisationId: string,
  asOf: string,
) {
  return rpc<Record<string, any>>("prepare_recurring_payment_schedules", {
    p_organisation_id: organisationId,
    p_as_of: asOf,
  });
}
export async function createObligation(
  organisationId: string,
  definition: Record<string, unknown>,
) {
  return rpc<any>("create_obligation", {
    p_organisation_id: organisationId,
    p_definition: definition,
  });
}
export async function submitObligationEvidence(
  organisationId: string,
  obligationId: string,
  evidenceType: string,
  summary: string,
  measuredValue: Record<string, unknown>,
) {
  return rpc<any>("submit_obligation_evidence", {
    p_organisation_id: organisationId,
    p_obligation_id: obligationId,
    p_evidence_type: evidenceType,
    p_summary: summary,
    p_measured_value: measuredValue,
  });
}
export async function decideObligation(
  organisationId: string,
  obligationId: string,
  evidenceId: string | null,
  decision: string,
  reason: string,
) {
  return rpc<any>("decide_obligation", {
    p_organisation_id: organisationId,
    p_obligation_id: obligationId,
    p_evidence_id: evidenceId,
    p_decision: decision,
    p_reason: reason,
  });
}
export async function registerAssetCoverage(
  organisationId: string,
  definition: Record<string, unknown>,
) {
  return rpc<any>("register_asset_coverage", {
    p_organisation_id: organisationId,
    p_definition: definition,
  });
}
export async function completeMaintenanceVisit(
  organisationId: string,
  visitId: string,
  input: {
    jobCardReference: string;
    engineerName: string;
    workPerformed: string;
    partsReplaced: string[];
    downtimeMinutes: number;
    serviceCreditAmount: number;
  },
) {
  return rpc<any>("complete_maintenance_visit", {
    p_organisation_id: organisationId,
    p_visit_id: visitId,
    p_job_card_reference: input.jobCardReference,
    p_engineer_name: input.engineerName,
    p_work_performed: input.workPerformed,
    p_parts_replaced: input.partsReplaced,
    p_downtime_minutes: input.downtimeMinutes,
    p_service_credit_amount: input.serviceCreditAmount,
    p_evidence_attachment_ids: [],
  });
}
export async function reportCoverageClaim(
  organisationId: string,
  coverageId: string,
  issueSummary: string,
) {
  return rpc<any>("report_coverage_claim", {
    p_organisation_id: organisationId,
    p_coverage_id: coverageId,
    p_issue_summary: issueSummary,
    p_owner_id: null,
  });
}
export async function advanceCoverageClaim(
  organisationId: string,
  claimId: string,
  newState: string,
  note: string,
) {
  return rpc<any>("advance_coverage_claim", {
    p_organisation_id: organisationId,
    p_claim_id: claimId,
    p_new_state: newState,
    p_note: note || null,
  });
}
export async function recordRequirementOutcome(
  organisationId: string,
  definition: Record<string, unknown>,
) {
  return rpc<any>("record_requirement_outcome", {
    p_organisation_id: organisationId,
    p_definition: definition,
  });
}
export async function runSpendFirewall(
  organisationId: string,
  requisitionId: string,
) {
  return rpc<any>("run_spend_firewall", {
    p_organisation_id: organisationId,
    p_requisition_id: requisitionId,
  });
}
export async function guidedBuyingWorkspace(
  organisationId: string,
  requisitionId: string,
) {
  return rpc<GuidedBuyingWorkspace>("guided_buying_workspace", {
    p_organisation_id: organisationId,
    p_requisition_id: requisitionId,
  });
}
export async function evaluateGuidedBuying(
  organisationId: string,
  requisitionId: string,
) {
  return rpc<GuidedBuyingWorkspace>("evaluate_guided_buying", {
    p_organisation_id: organisationId,
    p_requisition_id: requisitionId,
  });
}
export async function recordGuidedBuyingDecision(
  organisationId: string,
  requisitionId: string,
  evaluationId: string,
  input: {
    decision: string;
    sourceKind?: string;
    sourceId?: string;
    reason?: string;
  },
) {
  return rpc<GuidedBuyingDecision>("record_guided_buying_decision", {
    p_organisation_id: organisationId,
    p_requisition_id: requisitionId,
    p_evaluation_id: evaluationId,
    p_decision: input.decision,
    p_source_kind: input.sourceKind || null,
    p_source_id: input.sourceId || null,
    p_reason: input.reason || null,
  });
}
export async function reviewGuidedBuyingException(
  organisationId: string,
  requisitionId: string,
  evaluationId: string,
  decision: "approve_exception" | "deny_exception",
  reason: string,
) {
  return rpc<GuidedBuyingDecision>("review_guided_buying_exception", {
    p_organisation_id: organisationId,
    p_requisition_id: requisitionId,
    p_evaluation_id: evaluationId,
    p_decision: decision,
    p_reason: reason,
  });
}
export async function overrideSpendFirewall(
  organisationId: string,
  evaluationId: string,
  reason: string,
) {
  return rpc<any>("override_spend_firewall", {
    p_organisation_id: organisationId,
    p_evaluation_id: evaluationId,
    p_reason: reason,
  });
}
export async function runContinuousControls(organisationId: string) {
  return rpc<Record<string, any>>("run_continuous_controls", {
    p_organisation_id: organisationId,
  });
}
export async function refreshSupplierTrust(organisationId: string) {
  return rpc<Record<string, any>>("refresh_supplier_trust", {
    p_organisation_id: organisationId,
  });
}
export async function supplierTrustWorkspace(
  organisationId: string,
  vendorId?: string,
) {
  return rpc<{
    methodology: Record<string, any> | null;
    snapshots: Record<string, any>[];
    challenges: Record<string, any>[];
  }>("supplier_trust_workspace", {
    p_organisation_id: organisationId,
    p_vendor_id: vendorId || null,
  });
}
export async function submitSupplierTrustChallenge(
  organisationId: string,
  vendorId: string,
  snapshotId: string,
  statement: string,
  evidence: Record<string, unknown>[] = [],
) {
  return rpc<Record<string, any>>("submit_supplier_trust_challenge", {
    p_organisation_id: organisationId,
    p_vendor_id: vendorId,
    p_snapshot_id: snapshotId,
    p_statement: statement,
    p_evidence: evidence,
  });
}
export async function decideSupplierTrustChallenge(
  organisationId: string,
  challengeId: string,
  decision: "information_requested" | "accepted" | "rejected",
  reason: string,
) {
  return rpc<Record<string, any>>("decide_supplier_trust_challenge", {
    p_organisation_id: organisationId,
    p_challenge_id: challengeId,
    p_decision: decision,
    p_reason: reason,
  });
}
export type SupplierNetworkWorkspace = {
  profiles: Record<string, any>[];
  links: Record<string, any>[];
  claims: Record<string, any>[];
  verifications: Record<string, any>[];
  disputes: Record<string, any>[];
  merge_events: Record<string, any>[];
};
export async function supplierNetworkWorkspace(
  organisationId?: string,
  vendorId?: string,
) {
  return rpc<SupplierNetworkWorkspace>("supplier_network_workspace", {
    p_organisation_id: organisationId || null,
    p_vendor_id: vendorId || null,
  });
}
export async function createSupplierNetworkProfile(
  organisationId: string,
  vendorId: string,
  input: {
    displayName: string;
    legalName?: string;
    websiteUrl?: string;
    countryCode?: string;
  },
) {
  return rpc<Record<string, any>>("create_supplier_network_profile", {
    p_organisation_id: organisationId,
    p_vendor_id: vendorId,
    p_display_name: input.displayName,
    p_legal_name: input.legalName || null,
    p_website_url: input.websiteUrl || null,
    p_country_code: input.countryCode || null,
  });
}
export async function decideSupplierProfileLink(
  organisationId: string,
  vendorId: string,
  decision: "confirmed" | "rejected",
  reason: string,
) {
  return rpc<Record<string, any>>("decide_supplier_profile_link", {
    p_organisation_id: organisationId,
    p_vendor_id: vendorId,
    p_decision: decision,
    p_reason: reason,
  });
}
export async function requestSupplierProfileClaim(
  organisationId: string,
  vendorId: string,
  evidence: Record<string, unknown>[],
) {
  return rpc<Record<string, any>>("request_supplier_profile_claim", {
    p_organisation_id: organisationId,
    p_vendor_id: vendorId,
    p_evidence: evidence,
  });
}
export async function decideSupplierProfileClaim(
  claimId: string,
  decision: "approved" | "rejected",
  reason: string,
) {
  return rpc<Record<string, any>>("decide_supplier_profile_claim", {
    p_claim_id: claimId,
    p_decision: decision,
    p_reason: reason,
  });
}
export async function updateClaimedSupplierProfile(
  organisationId: string,
  vendorId: string,
  input: {
    displayName: string;
    legalName?: string;
    description: string;
    websiteUrl?: string;
    countryCode: string;
    publicContact: Record<string, unknown>;
    capabilityStatement: string;
    visibility: "private" | "buyers_only";
  },
) {
  return rpc<Record<string, any>>("update_claimed_supplier_profile", {
    p_organisation_id: organisationId,
    p_vendor_id: vendorId,
    p_display_name: input.displayName,
    p_legal_name: input.legalName || null,
    p_description: input.description,
    p_website_url: input.websiteUrl || null,
    p_country_code: input.countryCode,
    p_public_contact: input.publicContact,
    p_capability_statement: input.capabilityStatement,
    p_visibility: input.visibility,
  });
}
export async function recordSupplierVerification(
  profileId: string,
  status: "verified" | "revoked" | "expired",
  input: {
    checkedFields: Record<string, unknown>;
    evidenceProvenance: Record<string, unknown>;
    expiresAt?: string;
    reason: string;
  },
) {
  return rpc<Record<string, any>>("record_supplier_verification", {
    p_supplier_profile_id: profileId,
    p_status: status,
    p_checked_fields: input.checkedFields,
    p_evidence_provenance: input.evidenceProvenance,
    p_expires_at: input.expiresAt || null,
    p_reason: input.reason,
  });
}
export async function publishSupplierNetworkProfile(
  profileId: string,
  visibility: "private" | "buyers_only" | "public",
  reason: string,
) {
  return rpc<Record<string, any>>("publish_supplier_network_profile", {
    p_supplier_profile_id: profileId,
    p_visibility: visibility,
    p_reason: reason,
  });
}
export async function mergeSupplierNetworkProfiles(
  sourceProfileId: string,
  canonicalProfileId: string,
  reason: string,
) {
  return rpc<Record<string, any>>("merge_supplier_network_profiles", {
    p_source_profile_id: sourceProfileId,
    p_canonical_profile_id: canonicalProfileId,
    p_reason: reason,
  });
}
export async function createIntegrationConnection(
  organisationId: string,
  input: {
    provider: string;
    name: string;
    secretReference: string;
    eventNames: string[];
    routingConfig: Record<string, unknown>;
  },
) {
  return rpc<any>("create_integration_connection", {
    p_organisation_id: organisationId,
    p_provider: input.provider,
    p_name: input.name,
    p_secret_reference: input.secretReference,
    p_event_names: input.eventNames,
    p_routing_config: input.routingConfig,
  });
}
export async function setIntegrationConnectionStatus(
  organisationId: string,
  connectionId: string,
  status: string,
) {
  return rpc<any>("set_integration_connection_status", {
    p_organisation_id: organisationId,
    p_connection_id: connectionId,
    p_status: status,
  });
}
export async function askRaqoz(organisationId: string, question: string) {
  return rpc<Record<string, any>>("ask_raqoz", {
    p_organisation_id: organisationId,
    p_question: question,
  });
}
export async function generateManagementPack(
  organisationId: string,
  packType: string,
  periodStart: string,
  periodEnd: string,
) {
  return rpc<Record<string, any>>("generate_management_pack", {
    p_organisation_id: organisationId,
    p_pack_type: packType,
    p_period_start: periodStart,
    p_period_end: periodEnd,
  });
}
export async function transactionValueAssurancePack(
  organisationId: string,
  caseId: string,
) {
  return rpc<Record<string, unknown>>("transaction_value_assurance_pack", {
    p_organisation_id: organisationId,
    p_case_id: caseId,
  });
}

export async function actionEventWorkspace(organisationId: string) {
  const [requisitions, rfqs, orders, invoices, events, budgets] = await Promise.all([
    listRequisitions(organisationId),
    listRfqs(organisationId),
    listPurchaseOrders(organisationId),
    listInvoices(organisationId),
    listAudit(organisationId, 250),
    listBudgets(organisationId),
  ]);
  return { requisitions, rfqs, orders, invoices, events, budgets };
}

export async function requisitionBriefWorkspace(
  organisationId: string,
  requisitionId: string,
) {
  const [
    { data: requisition, error: requisitionError },
    { data: approvals, error: approvalsError },
    { data: attachments, error: attachmentsError },
    { data: rfq, error: rfqError },
  ] = await Promise.all([
    supabase
      .from("requisitions")
      .select(
        "*,requisition_lines(*),locations(name),profiles!requisitions_created_by_fkey(full_name,email)",
      )
      .eq("organisation_id", organisationId)
      .eq("id", requisitionId)
      .single(),
    supabase
      .from("requisition_approvals")
      .select(
        "*,profiles!requisition_approvals_approver_id_fkey(full_name,email)",
      )
      .eq("organisation_id", organisationId)
      .eq("requisition_id", requisitionId)
      .order("created_at"),
    supabase
      .from("attachments")
      .select("id,file_name,mime_type,byte_size,uploaded_by,created_at")
      .eq("organisation_id", organisationId)
      .eq("entity_type", "requisition")
      .eq("entity_id", requisitionId)
      .order("created_at"),
    supabase
      .from("rfqs")
      .select("*")
      .eq("organisation_id", organisationId)
      .eq("requisition_id", requisitionId)
      .maybeSingle(),
  ]);
  const firstError =
    requisitionError || approvalsError || attachmentsError || rfqError;
  if (firstError) throw new Error(firstError.message);
  const [budgetResult, passport] = await Promise.all([
    requisition?.budget_id
      ? supabase
          .from("budgets")
          .select("*,business_units(name,code),budget_commitments(amount,stage)")
          .eq("organisation_id", organisationId)
          .eq("id", requisition.budget_id)
          .maybeSingle()
      : Promise.resolve({ data: null, error: null }),
    controlPassportWorkspace(organisationId),
  ]);
  if (budgetResult.error) throw new Error(budgetResult.error.message);
  const controlCase = passport.cases.find(
    (item) =>
      item.request?.id === requisitionId ||
      item.request?.request_no === requisition?.request_no,
  ) || null;

  let quotes: any[] = [];
  let purchaseOrder: any = null;
  if (rfq) {
    const [quoteResult, orderResult] = await Promise.all([
      supabase
        .from("quotes")
        .select("*,vendors(name),quote_lines(*)")
        .eq("organisation_id", organisationId)
        .eq("rfq_id", rfq.id)
        .eq("is_current", true)
        .order("total"),
      supabase
        .from("purchase_orders")
        .select("*,vendors(name),po_lines(*)")
        .eq("organisation_id", organisationId)
        .eq("rfq_id", rfq.id)
        .maybeSingle(),
    ]);
    if (quoteResult.error || orderResult.error)
      throw new Error(quoteResult.error?.message || orderResult.error?.message);
    quotes = quoteResult.data || [];
    purchaseOrder = orderResult.data;
  }
  return {
    requisition,
    approvals: approvals || [],
    attachments: attachments || [],
    rfq,
    quotes,
    purchaseOrder,
    budget: budgetResult.data,
    controlPassport: {
      case: controlCase,
      policies: passport.policies,
    },
  };
}

export async function enhancedTransactionEvidencePack(
  organisationId: string,
  caseId: string,
) {
  const base = (await transactionValueAssurancePack(
    organisationId,
    caseId,
  )) as any;
  const requisitionId = base.case?.requisition_id;
  const [
    { data: requisition, error: requisitionError },
    { data: links, error: linksError },
  ] = await Promise.all([
    requisitionId
      ? supabase
          .from("requisitions")
          .select(
            "*,requisition_lines(*),locations(name),profiles!requisitions_created_by_fkey(full_name,email)",
          )
          .eq("organisation_id", organisationId)
          .eq("id", requisitionId)
          .single()
      : Promise.resolve({ data: null, error: null }),
    supabase
      .from("transaction_links")
      .select("*")
      .eq("organisation_id", organisationId)
      .eq("procurement_case_id", caseId)
      .order("created_at"),
  ]);
  if (requisitionError || linksError)
    throw new Error(requisitionError?.message || linksError?.message);
  const [
    { data: approvals, error: approvalError },
    { data: rfq, error: rfqError },
  ] = await Promise.all([
    requisitionId
      ? supabase
          .from("requisition_approvals")
          .select(
            "*,profiles!requisition_approvals_approver_id_fkey(full_name,email)",
          )
          .eq("organisation_id", organisationId)
          .eq("requisition_id", requisitionId)
          .order("created_at")
      : Promise.resolve({ data: [], error: null }),
    requisitionId
      ? supabase
          .from("rfqs")
          .select("*")
          .eq("organisation_id", organisationId)
          .eq("requisition_id", requisitionId)
          .maybeSingle()
      : Promise.resolve({ data: null, error: null }),
  ]);
  if (approvalError || rfqError)
    throw new Error(approvalError?.message || rfqError?.message);
  const purchaseOrderIds = (base.instruments || [])
    .map((row: any) => row.source_purchase_order_id)
    .filter(Boolean);
  const rfqId = rfq?.id;
  const entityIds = [requisitionId, rfqId, ...purchaseOrderIds].filter(Boolean);
  const [
    quotesResult,
    quotationEvidenceResult,
    receiptsResult,
    invoicesResult,
    attachmentsResult,
  ] =
    await Promise.all([
      rfqId
        ? supabase
            .from("quotes")
            .select("*,vendors(name),quote_lines(*)")
            .eq("organisation_id", organisationId)
            .eq("rfq_id", rfqId)
            .order("submitted_at")
        : Promise.resolve({ data: [], error: null }),
      rfqId
        ? supabase
            .from("quotation_evidence_workspace")
            .select("*")
            .eq("organisation_id", organisationId)
            .eq("rfq_id", rfqId)
            .order("created_at")
        : Promise.resolve({ data: [], error: null }),
      purchaseOrderIds.length
        ? supabase
            .from("goods_receipts")
            .select("*,goods_receipt_lines(*)")
            .eq("organisation_id", organisationId)
            .in("purchase_order_id", purchaseOrderIds)
            .order("received_at")
        : Promise.resolve({ data: [], error: null }),
      purchaseOrderIds.length
        ? supabase
            .from("invoices")
            .select("*")
            .eq("organisation_id", organisationId)
            .in("purchase_order_id", purchaseOrderIds)
            .order("created_at")
        : Promise.resolve({ data: [], error: null }),
      entityIds.length
        ? supabase
            .from("attachments")
            .select(
              "id,entity_type,entity_id,file_name,mime_type,byte_size,uploaded_by,created_at",
            )
            .eq("organisation_id", organisationId)
            .in("entity_id", entityIds)
            .order("created_at")
        : Promise.resolve({ data: [], error: null }),
    ]);
  const followupError =
    quotesResult.error ||
    quotationEvidenceResult.error ||
    receiptsResult.error ||
    invoicesResult.error ||
    attachmentsResult.error;
  if (followupError) throw new Error(followupError.message);
  return {
    export_format: "raqoz.transaction-evidence-pack",
    export_version: "3.2.0",
    generated_at: new Date().toISOString(),
    organisation_id: organisationId,
    source_pack: base,
    ancestry: {
      requisition,
      rfq,
      quotes: quotesResult.data || [],
      quotation_evidence: quotationEvidenceResult.data || [],
      approvals: approvals || [],
      transaction_links: links || [],
      goods_receipts: receiptsResult.data || [],
      invoices: invoicesResult.data || [],
      attachments: attachmentsResult.data || [],
    },
    quotationEvidenceCoverage: (quotationEvidenceResult.data || []).map(
      (row: any) => ({
        evidence_id: row.id,
        original_file: row.original_sha256
          ? "Available"
          : "Not recorded",
        provenance: row.classification ? "Available" : "Not recorded",
        extraction:
          row.extraction_state === "unavailable"
            ? "Unsupported by current integration"
            : row.extraction_state,
        corrections: row.corrections?.length
          ? "Available"
          : "Not recorded",
        verification: row.decisions?.length
          ? "Available"
          : "Not recorded",
        supplier_confirmation:
          row.supplier_confirmation_state === "configuration_required"
            ? "Configuration required"
            : row.supplier_confirmation_state,
        amendment_ancestry:
          row.parent_evidence_id || row.version_number > 1
            ? "Available"
            : "Not recorded",
        integrity_flags: row.integrity_flags?.length
          ? "Available"
          : "Not recorded",
        comparison_admission: row.comparison_state,
      }),
    ),
    integrity_scope: {
      database_manifest: base.value_assurance_manifest || null,
      covers: "source_pack only",
      client_envelope: "not included in the database manifest",
    },
  };
}

const ORGANISATION_EXPORT_TABLES: Record<string, string[]> = {
  organisation: ["organisations", "locations", "business_units"],
  people_and_controls: [
    "organisation_memberships",
    "responsibility_assignments",
    "approval_rules",
    "direct_purchase_policies",
  ],
  budgets: ["budgets", "budget_commitments", "budget_alerts"],
  suppliers_and_contracts: ["vendors", "contracts", "contract_items", "price_books"],
  requirements: [
    "requisitions",
    "requisition_lines",
    "requisition_approval_assignments",
    "requisition_approvals",
    "guided_buying_decisions",
    "spend_firewall_evaluations",
  ],
  sourcing: [
    "rfqs",
    "rfq_vendors",
    "quotes",
    "quote_lines",
    "quotation_evidence",
    "quotation_evidence_corrections",
    "quotation_evidence_decisions",
    "quotation_integrity_flags",
  ],
  orders_and_fulfilment: [
    "purchase_orders",
    "po_lines",
    "order_acknowledgements",
    "purchase_order_messages",
    "fulfilment_records",
  ],
  receipts: ["goods_receipts", "goods_receipt_lines"],
  invoices_and_matching: [
    "invoices",
    "supplier_invoice_submissions",
    "match_runs",
    "transaction_exceptions",
  ],
  settlement: [
    "payment_records",
    "commercial_adjustments",
    "commercial_payment_schedules",
    "commercial_schedule_approvals",
    "settlement_evidence",
    "reconciliation_records",
  ],
  recurring_and_obligations: [
    "recurring_commitments",
    "recurring_commitment_approvals",
    "commitment_periods",
    "obligation_ledger",
    "obligation_evidence",
    "obligation_decisions",
    "asset_coverages",
    "coverage_claims",
    "maintenance_visits",
  ],
  stock_and_assets: [
    "inventory_items",
    "inventory_lots",
    "inventory_movements",
    "reorder_templates",
    "replenishment_alerts",
    "assets",
    "asset_custody_events",
    "asset_consumable_links",
    "asset_consumption_events",
  ],
  reporting_and_assurance: [
    "organisation_reporting_profiles",
    "trade_compliance_details",
    "continuous_control_findings",
    "assurance_pack_runs",
    "requirement_outcome_proofs",
  ],
  evidence_and_audit: [
    "attachments",
    "procurement_document_snapshots",
    "audit_events",
  ],
};

export type OrganisationExportWorkspace = {
  organisation: Record<string, unknown>;
  modules: Record<string, Record<string, unknown>[]>;
  recordCounts: Record<string, number>;
};

export async function organisationExportWorkspace(
  organisationId: string,
): Promise<OrganisationExportWorkspace> {
  const moduleEntries = await Promise.all(
    Object.entries(ORGANISATION_EXPORT_TABLES).map(async ([module, tables]) => {
      const tableResults = await Promise.all(
        tables.map(async (table) => {
          const query = supabase.from(table).select("*").range(0, 9999);
          const { data, error } = await (table === "organisations"
            ? query.eq("id", organisationId)
            : query.eq("organisation_id", organisationId));
          if (error) throw new Error(`${table}: ${error.message}`);
          return (data || []).map((row) => ({ _table: table, ...row }));
        }),
      );
      return [module, tableResults.flat()] as const;
    }),
  );
  const modules = Object.fromEntries(moduleEntries);
  const organisation =
    modules.organisation.find((row) => row._table === "organisations") || {};
  return {
    organisation,
    modules,
    recordCounts: Object.fromEntries(
      Object.entries(modules).map(([module, rows]) => [module, rows.length]),
    ),
  };
}

export async function recordOrganisationExport(
  organisationId: string,
  input: {
    scope: string[];
    format: "json" | "csv_zip" | "excel_xml" | "print_pdf";
    reason: string;
    recordCounts: Record<string, number>;
    sha256: string;
  },
) {
  return rpc<Record<string, unknown>>("record_organisation_export", {
    p_organisation_id: organisationId,
    p_scope: input.scope,
    p_export_format: input.format,
    p_reason: input.reason,
    p_record_counts: input.recordCounts,
    p_sha256: input.sha256,
  });
}

export async function recordAccountingExport(
  organisationId: string,
  input: {
    provider: string;
    periodStart: string;
    periodEnd: string;
    rowCount: number;
    sha256: string;
  },
) {
  return rpc<Record<string, unknown>>("record_accounting_export", {
    p_organisation_id: organisationId,
    p_provider: input.provider,
    p_period_start: input.periodStart,
    p_period_end: input.periodEnd,
    p_row_count: input.rowCount,
    p_sha256: input.sha256,
  });
}

export type ControlPassportWorkspace = {
  cases: Array<Record<string, any>>;
  approved_suppliers: Array<Record<string, any>>;
  supplier_controls: Array<Record<string, any>>;
  policies: Array<Record<string, any>>;
};

export async function listRequisitionClarifications(
  organisationId: string,
  requisitionId: string,
) {
  const { data, error } = await supabase
    .from("requisition_clarification_events")
    .select(
      "id,organisation_id,requisition_id,event_kind,message,request_event_id,created_by,created_at,creator:profiles!requisition_clarification_events_created_by_fkey(full_name)",
    )
    .eq("organisation_id", organisationId)
    .eq("requisition_id", requisitionId)
    .order("created_at");
  return unwrap(data, error) as Array<Record<string, any>>;
}

export async function requestRequisitionClarification(
  organisationId: string,
  requisitionId: string,
  message: string,
) {
  return rpc<Record<string, any>>("request_requisition_clarification", {
    p_organisation_id: organisationId,
    p_requisition_id: requisitionId,
    p_message: message,
  });
}

export async function respondRequisitionClarification(
  organisationId: string,
  requestEventId: string,
  message: string,
) {
  return rpc<Record<string, any>>("respond_requisition_clarification", {
    p_organisation_id: organisationId,
    p_request_event_id: requestEventId,
    p_message: message,
  });
}

export async function controlPassportWorkspace(
  organisationId: string,
  caseId?: string,
) {
  return rpc<ControlPassportWorkspace>("control_passport_workspace", {
    p_organisation_id: organisationId,
    p_case_id: caseId || null,
  });
}

export async function evaluateControlPassport(
  organisationId: string,
  caseId: string,
  checkpoint = "closure",
) {
  return rpc<Record<string, any>>("evaluate_control_passport", {
    p_organisation_id: organisationId,
    p_case_id: caseId,
    p_checkpoint: checkpoint,
  });
}

export async function configureSupplierControl(
  organisationId: string,
  input: {
    vendorId: string;
    supplierType: string;
    category: string;
    criticality: string;
    riskClassification: string;
    qualificationStatus: string;
    ownerId?: string;
    approverId?: string;
    validUntil?: string;
    nextReviewOn?: string;
    rationale: string;
  },
) {
  return rpc<Record<string, any>>("configure_supplier_control", {
    p_organisation_id: organisationId,
    p_vendor_id: input.vendorId,
    p_supplier_type: input.supplierType,
    p_category: input.category,
    p_criticality: input.criticality,
    p_risk_classification: input.riskClassification,
    p_qualification_status: input.qualificationStatus,
    p_owner_id: input.ownerId || null,
    p_approver_id: input.approverId || null,
    p_valid_until: input.validUntil || null,
    p_next_review_on: input.nextReviewOn || null,
    p_rationale: input.rationale,
  });
}

export async function createControlPolicyVersion(
  organisationId: string,
  input: {
    policyId?: string;
    name: string;
    scope: Record<string, unknown>;
    rules: Array<Record<string, unknown>>;
    requiredEvidence: Array<Record<string, unknown>>;
    rationale: string;
  },
) {
  return rpc<Record<string, any>>("create_control_policy_version", {
    p_organisation_id: organisationId,
    p_policy_id: input.policyId || null,
    p_name: input.name,
    p_scope: input.scope,
    p_rules: input.rules,
    p_required_evidence: input.requiredEvidence,
    p_rationale: input.rationale,
  });
}

export async function internalControlsWorkspace(organisationId: string) {
  return rpc<InternalControlsWorkspace>("internal_controls_workspace", {
    p_organisation_id: organisationId,
  });
}

export async function createInternalControlPolicyVersion(
  organisationId: string,
  input: {
    policyId?: string;
    name: string;
    parentVersionId?: string;
    inheritanceMode: "merge" | "replace";
    rules: InternalControlRule[];
    rationale: string;
  },
) {
  return rpc<InternalControlPolicyVersion>(
    "create_internal_control_policy_version",
    {
      p_organisation_id: organisationId,
      p_policy_id: input.policyId || null,
      p_name: input.name,
      p_scope: { organisation_wide: true },
      p_parent_version_id: input.parentVersionId || null,
      p_inheritance_mode: input.inheritanceMode,
      p_rules: input.rules,
      p_required_evidence: [
        "policy_version_ids",
        "matched_rule_codes",
        "evaluation_sha256",
        "actor_and_timestamp",
      ],
      p_rationale: input.rationale,
    },
  );
}

export async function configureInternalControls(
  organisationId: string,
  enforcementMode: "disabled" | "observe" | "enforce",
  defaultPolicyVersionId: string | null,
  reason: string,
) {
  return rpc<Record<string, unknown>>("configure_internal_controls", {
    p_organisation_id: organisationId,
    p_enforcement_mode: enforcementMode,
    p_default_policy_version_id: defaultPolicyVersionId,
    p_reason: reason,
  });
}

export async function simulateInternalControls(
  organisationId: string,
  policyVersionId: string,
  cases: Array<Record<string, unknown>>,
) {
  return rpc<Record<string, any>>("simulate_internal_controls", {
    p_organisation_id: organisationId,
    p_policy_version_id: policyVersionId,
    p_cases: cases,
  });
}

export async function requestControlDeviation(
  organisationId: string,
  input: {
    subjectType: "requisition" | "rfq" | "award";
    subjectId: string;
    deviationKind: string;
    controlRuleId: string;
    context: Record<string, unknown>;
    reason: string;
    validUntil?: string;
  },
) {
  return rpc<Record<string, any>>("request_control_deviation", {
    p_organisation_id: organisationId,
    p_subject_type: input.subjectType,
    p_subject_id: input.subjectId,
    p_deviation_kind: input.deviationKind,
    p_control_rule_id: input.controlRuleId,
    p_context: input.context,
    p_reason: input.reason,
    p_valid_until: input.validUntil || null,
  });
}

export async function decideControlDeviation(
  organisationId: string,
  deviationId: string,
  decision: "approved" | "rejected",
  reason: string,
) {
  return rpc<Record<string, any>>("decide_control_deviation", {
    p_organisation_id: organisationId,
    p_deviation_id: deviationId,
    p_decision: decision,
    p_reason: reason,
  });
}

export async function evaluateAwardControls(
  organisationId: string,
  rfqId: string,
  quoteId: string,
) {
  return rpc<Record<string, any>>("evaluate_award_controls", {
    p_organisation_id: organisationId,
    p_rfq_id: rfqId,
    p_quote_id: quoteId,
  });
}

export async function approveControlPolicyVersion(
  organisationId: string,
  policyVersionId: string,
  effectiveFrom: string,
  reason: string,
) {
  return rpc<Record<string, any>>("approve_control_policy_version", {
    p_organisation_id: organisationId,
    p_policy_version_id: policyVersionId,
    p_effective_from: effectiveFrom,
    p_reason: reason,
  });
}

export async function industryTemplateWorkspace(organisationId: string) {
  const [templates, activations, terminology, unitTypes, unitAccess] =
    await Promise.all([
      supabase
        .from("industry_template_versions")
        .select("*")
        .in("status", ["pilot", "available"])
        .order("industry")
        .order("version", { ascending: false }),
      supabase
        .from("organisation_industry_template_activations")
        .select("*")
        .eq("organisation_id", organisationId)
        .order("activated_at", { ascending: false }),
      supabase
        .from("organisation_terminology")
        .select("*")
        .eq("organisation_id", organisationId)
        .eq("active", true)
        .order("term_key"),
      supabase
        .from("organisation_operating_unit_types")
        .select("*")
        .eq("organisation_id", organisationId)
        .eq("active", true)
        .order("sort_order"),
      supabase
        .from("operating_unit_access_assignments")
        .select("*")
        .eq("organisation_id", organisationId)
        .eq("active", true),
    ]);
  const failed = [templates, activations, terminology, unitTypes, unitAccess].find(
    (result) => result.error,
  );
  if (failed?.error) throw new Error(failed.error.message);
  return {
    templates: templates.data || [],
    activations: activations.data || [],
    terminology: terminology.data || [],
    unitTypes: unitTypes.data || [],
    unitAccess: unitAccess.data || [],
  };
}

export async function activateIndustryTemplate(
  organisationId: string,
  templateVersionId: string,
) {
  return rpc<Record<string, any>>("activate_industry_template", {
    p_organisation_id: organisationId,
    p_template_version_id: templateVersionId,
  });
}

export async function configureOperatingUnitType(
  organisationId: string,
  input: {
    typeKey: string;
    singular: string;
    plural: string;
    description: string;
    sortOrder?: number;
  },
) {
  return rpc<Record<string, any>>("configure_operating_unit_type", {
    p_organisation_id: organisationId,
    p_type_key: input.typeKey,
    p_singular: input.singular,
    p_plural: input.plural,
    p_description: input.description,
    p_sort_order: input.sortOrder || 0,
  });
}


export async function controlledPilotWorkspace(organisationId: string) {
  const [pilots, templates, events, measurements, validations] = await Promise.all([
    supabase.from("vertical_pilots").select("*").eq("organisation_id", organisationId).order("proposed_at", { ascending: false }),
    supabase.from("industry_template_versions").select("id,name,industry,version,status").in("status", ["pilot", "available"]).order("industry"),
    supabase.from("vertical_pilot_events").select("*").eq("organisation_id", organisationId).order("occurred_at", { ascending: false }),
    supabase.from("vertical_pilot_measurements").select("*").eq("organisation_id", organisationId).order("recorded_at", { ascending: false }),
    supabase.from("vertical_golden_journey_validations").select("*").eq("organisation_id", organisationId).order("validated_at", { ascending: false }),
  ]);
  const failed = [pilots, templates, events, measurements, validations].find((result) => result.error);
  if (failed?.error) throw new Error(failed.error.message);
  return {
    pilots: pilots.data || [], templates: templates.data || [], events: events.data || [],
    measurements: measurements.data || [], validations: validations.data || [],
  };
}

export async function createVerticalPilot(organisationId: string, input: {
  templateVersionId: string; name: string; designPartnerReference: string;
  scope: Record<string, unknown>; migrationPlan: string; supportOwnerId: string;
  rollbackPlan: string; privacyRequirements: string;
}) {
  return rpc<Record<string, any>>("create_vertical_pilot", {
    p_organisation_id: organisationId, p_template_version_id: input.templateVersionId,
    p_name: input.name, p_design_partner_reference: input.designPartnerReference,
    p_scope: input.scope, p_existing_system_boundary: [], p_users_and_roles: [],
    p_transaction_types: [], p_baseline_plan: [], p_success_measures: [],
    p_migration_plan: input.migrationPlan, p_support_owner_id: input.supportOwnerId,
    p_rollback_plan: input.rollbackPlan, p_privacy_requirements: input.privacyRequirements,
  });
}

export async function transitionVerticalPilot(
  organisationId: string, pilotId: string, nextState: string, reason: string,
) {
  return rpc<Record<string, any>>("transition_vertical_pilot", {
    p_organisation_id: organisationId, p_pilot_id: pilotId,
    p_new_state: nextState, p_reason: reason,
  });
}


export type PlatformSupportSeverity = "low" | "normal" | "high" | "critical";
export type PlatformSupportStatus =
  | "open"
  | "triaged"
  | "in_progress"
  | "waiting_customer"
  | "resolved"
  | "closed";

export async function organisationAdminConsoleSnapshot(
  organisationId: string,
) {
  return rpc<any>("organisation_admin_console_snapshot", {
    p_organisation_id: organisationId,
  });
}

export async function platformMissionControlSnapshot() {
  return rpc<any>("platform_mission_control_snapshot", {});
}

export async function platformSupportWorkspace(
  organisationId: string | null = null,
) {
  return rpc<{ cases: any[]; events: any[] }>("platform_support_workspace", {
    p_organisation_id: organisationId,
  });
}

export async function requestPlatformSupportCase(
  organisationId: string,
  input: {
    category: string;
    severity: PlatformSupportSeverity;
    subject: string;
    description: string;
  },
) {
  return rpc<any>("request_platform_support_case", {
    p_organisation_id: organisationId,
    p_category: input.category,
    p_severity: input.severity,
    p_subject: input.subject,
    p_description: input.description,
  });
}

export async function addPlatformSupportCaseNote(
  caseId: string,
  note: string,
) {
  return rpc<number>("add_platform_support_case_note", {
    p_case_id: caseId,
    p_note: note,
  });
}

export async function updatePlatformSupportCase(
  caseId: string,
  input: {
    status: PlatformSupportStatus;
    severity: PlatformSupportSeverity;
    assignedTo?: string | null;
    reason: string;
    resolution?: string | null;
  },
) {
  return rpc<any>("update_platform_support_case", {
    p_case_id: caseId,
    p_status: input.status,
    p_severity: input.severity,
    p_assigned_to: input.assignedTo || null,
    p_reason: input.reason,
    p_resolution: input.resolution || null,
  });
}


export async function platformOrganisationControlSnapshot(
  organisationId: string,
) {
  return rpc<any>("platform_organisation_control_snapshot", {
    p_organisation_id: organisationId,
  });
}

export async function requestCompanyAdministrator(
  organisationId: string,
  input: { email: string; fullName: string; reason: string },
) {
  const { data, error } = await supabase.functions.invoke("invite-user", {
    body: { action: "platform_request", organisationId, ...input },
  });
  return unwrap(data, error);
}

export async function decideCompanyAdministratorRequest(
  requestId: string,
  decision: "approve" | "reject",
  reason: string,
) {
  const { data, error } = await supabase.functions.invoke("invite-user", {
    body: {
      action: decision === "approve" ? "platform_approve" : "platform_reject",
      requestId,
      reason,
    },
  });
  return unwrap(data, error);
}

export async function organisationAdminGovernanceSnapshot(
  organisationId: string,
) {
  return rpc<any>("organisation_admin_governance_snapshot", {
    p_organisation_id: organisationId,
  });
}
